import { Optional } from "../../utils/Optional";
import { searchWord, WordFinal } from "../WordBase";
import * as rt from "runtypes";

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export const GreekVerbNumberPerson = rt.Union(
  rt.Literal("s1"),
  rt.Literal("s2"),
  rt.Literal("s3"),
  rt.Literal("d2"),
  rt.Literal("d3"),
  rt.Literal("p1"),
  rt.Literal("p2"),
  rt.Literal("p3")
);
export type GreekVerbNumberPerson = rt.Static<typeof GreekVerbNumberPerson>;

export const GreekConjugatedTense = rt.Dictionary(WordFinal, GreekVerbNumberPerson);
export type GreekConjugatedTense = rt.Static<typeof GreekConjugatedTense>;

export const GreekInfinitiveAM = rt.Record({
  active: rt.String,
  middle: rt.String,
});
export type GreekInfinitiveAM = rt.Static<typeof GreekInfinitiveAM>;

export const GreekParticipleAM = rt.Record({
  active: rt.String,
  middle: rt.String,
});
export type GreekParticipleAM = rt.Static<typeof GreekParticipleAM>;

export const GreekInfinitiveAMP = rt.Record({
  active: rt.String,
  middle: rt.String,
  passive: rt.String,
});
export type GreekInfinitiveAMP = rt.Static<typeof GreekInfinitiveAMP>;

export const GreekParticipleAMP = rt.Record({
  active: rt.String,
  middle: rt.String,
  passive: rt.String,
});
export type GreekParticipleAMP = rt.Static<typeof GreekParticipleAMP>;

export interface GreekVerb {
  type: "verb";
  ground: string;
  passport: string;
  dutch: string;
  explicitConjugations?: {
    present?: {
      active?: {
        indicative?: Optional<GreekConjugatedTense>;
        subjunctive?: Optional<GreekConjugatedTense>;
        optative?: Optional<GreekConjugatedTense>;
        imperative?: Optional<GreekConjugatedTense>;
      },
      middlePassive?: {
        indicative?: Optional<GreekConjugatedTense>;
        subjunctive?: Optional<GreekConjugatedTense>;
        optative?: Optional<GreekConjugatedTense>;
        imperative?: Optional<GreekConjugatedTense>;
      },
      infinitive?: Optional<GreekInfinitiveAM>,
      participle?: {
        m?: Optional<GreekParticipleAM>,
        f?: Optional<GreekParticipleAM>,
        n?: Optional<GreekParticipleAM>,
      },
    },
    imperfect?: {
      active?: {
        indicative?: Optional<GreekConjugatedTense>;
      },
      middlePassive?: {
        indicative?: Optional<GreekConjugatedTense>;
      },
    },
    future?: {
      active?: {
        indicative?: Optional<GreekConjugatedTense>;
        optative?: Optional<GreekConjugatedTense>;
      },
      middle?: {
        indicative?: Optional<GreekConjugatedTense>;
        optative?: Optional<GreekConjugatedTense>;
      },
      passive?: {
        indicative?: Optional<GreekConjugatedTense>;
        optative?: Optional<GreekConjugatedTense>;
      },
      infinitive?: Optional<GreekInfinitiveAMP>,
      participle?: {
        m?: Optional<GreekParticipleAMP>,
        f?: Optional<GreekParticipleAMP>,
        n?: Optional<GreekParticipleAMP>,
      },
    },
  },


}

export const GREEK_VERBS: GreekVerb[] = [
  {
    // https://en.wiktionary.org/wiki/%CE%B3%CF%81%CE%AC%CF%86%CF%89
    type: "verb",
    ground: "γρἀφειν",
    passport: "γρἀφω",
    dutch: "schrijven",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "γρᾰ́φω",
            "s2": "γρᾰ́φεις",
            "s3": "γρᾰ́φει",
            "d2": "γρᾰ́φετον",
            "d3": "γρᾰ́φετον",
            "p1": "γρᾰ́φομεν",
            "p2": "γρᾰ́φετε",
            "p3": "γρᾰ́φουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BB%CF%8D%CF%89
    type: "verb",
    ground: "λὐειν",
    passport: "λὐω",
    dutch: "losmaken",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "λῡ́ω",
            "s2": "λῡ́ω",
            "s3": "λῡ́ει",
            "d2": "λῡ́ετον",
            "d3": "λῡ́ετον",
            "p1": "λῡ́ομεν",
            "p2": "λῡ́ετε",
            "p3": "λῡ́ουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B5%E1%BC%B0%CE%BC%CE%AF
    type: "verb",
    ground: "εἶναι",
    passport: "εἰμί",
    dutch: "zijn, zich bevinden, bestaan",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "εἰμί",
            "s2": "εἶ",
            "s3": ["ἐστί", "ἐστίν"],
            "d2": "ἐστόν",
            "d3": "ἐστόν",
            "p1": "ἐσμέν",
            "p2": "ἐστέ",
            "p3": ["εἰσί", "εἰσίν"],
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CE%BA%CE%BF%CF%8D%CF%89
    type: "verb",
    dutch: "horen, luisteren",
    ground: "ᾰ̓κούειν",
    passport: "ᾰ̓κούω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ᾰ̓κούω",
            "s2": "ᾰ̓κούεις",
            "s3": "ᾰ̓κούει",
            "d2": "ᾰ̓κούετον",
            "d3": "ᾰ̓κούετον",
            "p1": "ᾰ̓κούομεν",
            "p2": "ᾰ̓κούετε",
            "p3": "ᾰ̓κούουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B2%CE%BB%CE%AD%CF%80%CF%89
    type: "verb",
    dutch: "kijken",
    ground: "βλέπειν",
    passport: "βλέπω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "βλέπω",
            "s2": "βλέπεις",
            "s3": "βλέπει",
            "d2": "βλέπετον",
            "d3": "βλέπετον",
            "p1": "βλέπομεν",
            "p2": "βλέπετε",
            "p3": "βλέπουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%90%CF%83%CE%B8%CE%AF%CF%89
    type: "verb",
    dutch: "eten",
    ground: "ἐσθῐ́ειν",
    passport: "ἐσθῐ́ω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ἐσθῐ́ω",
            "s2": "ἐσθῐ́εις",
            "s3": "ἐσθῐ́ει",
            "d2": "ἐσθῐ́ετον",
            "d3": "ἐσθῐ́ετον",
            "p1": "ἐσθῐ́ομεν",
            "p2": "ἐσθῐ́ετε",
            "p3": "ἐσθῐ́ουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%94%CF%87%CF%89
    type: "verb",
    dutch: "hebben, houden",
    ground: "ἔχειν",
    passport: "ἔχω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ἔχω",
            "s2": "ἔχεις",
            "s3": "ἔχει",
            "d2": "ἔχετον",
            "d3": "ἔχετον",
            "p1": "ἔχομεν",
            "p2": "ἔχετε",
            "p3": "ἔχουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BB%CE%AD%CE%B3%CF%89
    type: "verb",
    dutch: "zeggen, spreken",
    ground: "λέγειν",
    passport: "λέγω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "λέγω",
            "s2": "λέγεις",
            "s3": "λέγει",
            "d2": "λέγετον",
            "d3": "λέγετον",
            "p1": "λέγομεν",
            "p2": "λέγετε",
            "p3": "λέγουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%AD%CE%BC%CF%80%CF%89
    type: "verb",
    dutch: "zenden",
    ground: "πέμπειν",
    passport: "πέμπω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "πέμπω",
            "s2": "πέμπεις",
            "s3": "πέμπει",
            "d2": "πέμπετον",
            "d3": "πέμπετον",
            "p1": "πέμπομεν",
            "p2": "πέμπετε",
            "p3": "πέμπουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%AF%CE%BD%CF%89
    type: "verb",
    dutch: "drinken",
    ground: "πῑ́νειν",
    passport: "πῑ́νω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "πῑ́νω",
            "s2": "πῑ́νεις",
            "s3": "πῑ́νει",
            "d2": "πῑ́νετον",
            "d3": "πῑ́νετον",
            "p1": "πῑ́νομεν",
            "p2": "πῑ́νετε",
            "p3": "πῑ́νουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%84%CF%81%CE%AD%CF%86%CF%89
    type: "verb",
    dutch: "voeden, opvoeden",
    ground: "τρέφειν",
    passport: "τρέφω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "τρέφω",
            "s2": "τρέφεις",
            "s3": "τρέφει",
            "d2": "τρέφετον",
            "d3": "τρέφετον",
            "p1": "τρέφομεν",
            "p2": "τρέφετε",
            "p3": "τρέφουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%84%CE%B3%CF%89
    type: "verb",
    dutch: "voeren, brengen",
    ground: "ᾰ̓́γειν",
    passport: "ᾰ̓́γω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ᾰ̓́γω",
            "s2": "ᾰ̓́γεις",
            "s3": "ᾰ̓́γει",
            "d2": "ᾰ̓́γετον",
            "d3": "ᾰ̓́γετον",
            "p1": "ᾰ̓́γομεν",
            "p2": "ᾰ̓́γετε",
            "p3": "ᾰ̓́γουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B5%E1%BD%91%CF%81%CE%AF%CF%83%CE%BA%CF%89
    type: "verb",
    dutch: "vinden",
    ground: "εὑρῐ́σκειν",
    passport: "εὑρῐ́σκω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "εὑρῐ́σκω",
            "s2": "εὑρῐ́σκεις",
            "s3": "εὑρῐ́σκει",
            "d2": "εὑρῐ́σκετον",
            "d3": "εὑρῐ́σκετον",
            "p1": "εὑρῐ́σκομεν",
            "p2": "εὑρῐ́σκετε",
            "p3": "εὑρῐ́σκουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%87%CE%B1%CE%AF%CF%81%CF%89
    type: "verb",
    dutch: "zich verheugen (over), blij zijn (met)",
    ground: "χαίρειν",
    passport: "χαίρω (+ datief)",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "χαίρω",
            "s2": "χαίρεις",
            "s3": "χαίρει",
            "d2": "χαίρετον",
            "d3": "χαίρετον",
            "p1": "χαίρομεν",
            "p2": "χαίρετε",
            "p3": "χαίρουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CF%80%CE%BF%CE%BA%CF%84%CE%B5%CE%AF%CE%BD%CF%89
    type: "verb",
    dutch: "doden",
    ground: "ἀποκτείνειν",
    passport: "ἀποκτείνω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ἀποκτείνω",
            "s2": "ἀποκτείνεις",
            "s3": "ἀποκτείνει",
            "d2": "ἀποκτείνετον",
            "d3": "ἀποκτείνετον",
            "p1": "ἀποκτείνομεν",
            "p2": "ἀποκτείνετε",
            "p3": "ἀποκτείνουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B2%CE%B1%CE%AF%CE%BD%CF%89
    type: "verb",
    dutch: "gaan",
    ground: "βαίνειν",
    passport: "βαίνω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "βαίνω",
            "s2": "βαίνεις",
            "s3": "βαίνει",
            "d2": "βαίνετον",
            "d3": "βαίνετον",
            "p1": "βαίνομεν",
            "p2": "βαίνετε",
            "p3": "βαίνουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B8%CE%B1%CF%85%CE%BC%CE%AC%CE%B6%CF%89
    type: "verb",
    dutch: "verwonderd zijn, bewonderen",
    ground: "θαυμᾰ́ζειν",
    passport: "θαυμᾰ́ζω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "θαυμᾰ́ζω",
            "s2": "θαυμᾰ́ζεις",
            "s3": "θαυμᾰ́ζει",
            "d2": "θαυμᾰ́ζετον",
            "d3": "θαυμᾰ́ζετον",
            "p1": "θαυμᾰ́ζομεν",
            "p2": "θαυμᾰ́ζετε",
            "p3": "θαυμᾰ́ζουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%B1%CF%81%CE%AD%CF%87%CF%89
    type: "verb",
    dutch: "geven, verschaffen",
    ground: "πᾰρέχειν",
    passport: "πᾰρέχω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "πᾰρέχω",
            "s2": "πᾰρέχεις",
            "s3": "πᾰρέχει",
            "d2": "πᾰρέχετον",
            "d3": "πᾰρέχετον",
            "p1": "πᾰρέχομεν",
            "p2": "πᾰρέχετε",
            "p3": "πᾰρέχουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%86%CE%AD%CF%81%CF%89
    type: "verb",
    dutch: "dragen, verdragen",
    ground: "φέρειν",
    passport: "φέρω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "φέρω",
            "s2": "φέρεις",
            "s3": "φέρει",
            "d2": "φέρετον",
            "d3": "φέρετον",
            "p1": "φέρομεν",
            "p2": "φέρετε",
            "p3": "φέρουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%86%CE%B5%CF%8D%CE%B3%CF%89
    type: "verb",
    dutch: "vluchten",
    ground: "φεύγειν",
    passport: "φεύγω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "φεύγω",
            "s2": "φεύγεις",
            "s3": "φεύγει",
            "d2": "φεύγετον",
            "d3": "φεύγετον",
            "p1": "φεύγομεν",
            "p2": "φεύγετε",
            "p3": "φεύγουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%90%CE%B8%CE%AD%CE%BB%CF%89
    type: "verb",
    dutch: "willen",
    ground: "ἐθέλειν",
    passport: "ἐθέλω / θέλω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ἐθέλω",
            "s2": "ἐθέλεις",
            "s3": "ἐθέλει",
            "d2": "ἐθέλετον",
            "d3": "ἐθέλετον",
            "p1": "ἐθέλομεν",
            "p2": "ἐθέλετε",
            "p3": "ἐθέλουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BD%80%CE%BD%CE%BF%CE%BC%CE%AC%CE%B6%CF%89
    type: "verb",
    dutch: "noemen; pass.: heten",
    ground: "ὀνομᾰ́ζειν",
    passport: "ὀνομᾰ́ζω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ὀνομᾰ́ζω",
            "s2": "ὀνομᾰ́ζεις",
            "s3": "ὀνομᾰ́ζει",
            "d2": "ὀνομᾰ́ζετον",
            "d3": "ὀνομᾰ́ζετον",
            "p1": "ὀνομᾰ́ζομεν",
            "p2": "ὀνομᾰ́ζετε",
            "p3": "ὀνομᾰ́ζουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%83%E1%BF%B4%CE%B6%CF%89
    type: "verb",
    dutch: "redden, bewaren",
    ground: "σῴζειν",
    passport: "σῴζω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "σῴζω",
            "s2": "σῴζεις",
            "s3": "σῴζει",
            "d2": "σῴζετον",
            "d3": "σῴζετον",
            "p1": "σῴζομεν",
            "p2": "σῴζετε",
            "p3": "σῴζουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%A5%CE%BA%CF%89
    type: "verb",
    dutch: "komen, gekomen zijn",
    ground: "ἥκειν",
    passport: "ἥκω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ἥκω",
            "s2": "ἥκεις",
            "s3": "ἥκει",
            "d2": "ἥκετον",
            "d3": "ἥκετον",
            "p1": "ἥκομεν",
            "p2": "ἥκετε",
            "p3": "ἥκουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B2%CE%AC%CE%BB%CE%BB%CF%89
    type: "verb",
    dutch: "werpen, treffen",
    ground: "βᾰ́λλειν",
    passport: "βᾰ́λλω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "βᾰ́λλω",
            "s2": "βᾰ́λλεις",
            "s3": "βᾰ́λλει",
            "d2": "βᾰ́λλετον",
            "d3": "βᾰ́λλετον",
            "p1": "βᾰ́λλομεν",
            "p2": "βᾰ́λλετε",
            "p3": "βᾰ́λλουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BB%CE%B5%CE%AF%CF%80%CF%89
    type: "verb",
    dutch: "verlaten, achterlaten",
    ground: "λείπειν",
    passport: "λείπω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "λείπω",
            "s2": "λείπεις",
            "s3": "λείπει",
            "d2": "λείπετον",
            "d3": "λείπετον",
            "p1": "λείπομεν",
            "p2": "λείπετε",
            "p3": "λείπουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%84%CE%AF%CE%BA%CF%84%CF%89
    type: "verb",
    dutch: "baren, ter wereld brengen",
    ground: "τῐ́κτειν",
    passport: "τῐ́κτω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "τῐ́κτω",
            "s2": "τῐ́κτεις",
            "s3": "τῐ́κτει",
            "d2": "τῐ́κτετον",
            "d3": "τῐ́κτετον",
            "p1": "τῐ́κτομεν",
            "p2": "τῐ́κτετε",
            "p3": "τῐ́κτουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CE%B9%CE%B1%CF%86%CE%AD%CF%81%CF%89
    type: "verb",
    dutch: "tot het einde dragen, verschillen",
    ground: "δῐᾰφέρειν",
    passport: "δῐᾰφέρω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "δῐᾰφέρω",
            "s2": "δῐᾰφέρεις",
            "s3": "δῐᾰφέρει",
            "d2": "δῐᾰφέρετον",
            "d3": "δῐᾰφέρετον",
            "p1": "δῐᾰφέρομεν",
            "p2": "δῐᾰφέρετε",
            "p3": "δῐᾰφέρουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CE%BD%CE%B1%CE%B3%CE%B9%CE%B3%CE%BD%CF%8E%CF%83%CE%BA%CF%89
    type: "verb",
    dutch: "herkennen, lezen",
    ground: "ᾰ̓νᾰγῐγνώσκειν",
    passport: "ᾰ̓νᾰγῐγνώσκω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "ᾰ̓νᾰγῐγνώσκω",
            "s2": "ᾰ̓νᾰγῐγνώσκεις",
            "s3": "ᾰ̓νᾰγῐγνώσκει",
            "d2": "ᾰ̓νᾰγῐγνώσκετον",
            "d3": "ᾰ̓νᾰγῐγνώσκετον",
            "p1": "ᾰ̓νᾰγῐγνώσκομεν",
            "p2": "ᾰ̓νᾰγῐγνώσκετε",
            "p3": "ᾰ̓νᾰγῐγνώσκουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BB%CE%B1%CE%BC%CE%B2%CE%AC%CE%BD%CF%89
    type: "verb",
    dutch: "nemen, krijgen",
    ground: "λᾰμβᾰ́νειν",
    passport: "λᾰμβᾰ́νω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "λᾰμβᾰ́νω",
            "s2": "λᾰμβᾰ́νεις",
            "s3": "λᾰμβᾰ́νει",
            "d2": "λᾰμβᾰ́νετον",
            "d3": "λᾰμβᾰ́νετον",
            "p1": "λᾰμβᾰ́νομεν",
            "p2": "λᾰμβᾰ́νετε",
            "p3": "λᾰμβᾰ́νουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CF%81%CE%AC%CF%83%CF%83%CF%89
    type: "verb",
    dutch: "verrichten, tot stand brengen",
    ground: "πρᾱ́ττειν",
    passport: "πρᾱ́ττω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "πρᾱ́ττω",
            "s2": "πρᾱ́ττεις",
            "s3": "πρᾱ́ττει",
            "d2": "πρᾱ́ττετον",
            "d3": "πρᾱ́ττετον",
            "p1": "πρᾱ́ττομεν",
            "p2": "πρᾱ́ττετε",
            "p3": "πρᾱ́ττουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B3%CE%B9%CE%B3%CE%BD%CF%8E%CF%83%CE%BA%CF%89
    type: "verb",
    dutch: "leren kennen, begrijpen",
    ground: "γιγνώσκειν",
    passport: "γιγνώσκω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "γιγνώσκω",
            "s2": "γιγνώσκεις",
            "s3": "γιγνώσκει",
            "d2": "γιγνώσκετον",
            "d3": "γιγνώσκετον",
            "p1": "γιγνώσκομεν",
            "p2": "γιγνώσκετε",
            "p3": "γιγνώσκουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BC%CE%AD%CE%BD%CF%89
    type: "verb",
    dutch: "blijven, verblijven",
    ground: "μένειν",
    passport: "μένω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "μένω",
            "s2": "μένεις",
            "s3": "μένει",
            "d2": "μένετον",
            "d3": "μένετον",
            "p1": "μένομεν",
            "p2": "μένετε",
            "p3": "μένουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CE%B9%CE%B1%CF%86%CE%B8%CE%B5%CE%AF%CF%81%CF%89
    type: "verb",
    dutch: "vernielen, bederven",
    ground: "δῐᾰφθείρειν",
    passport: "δῐᾰφθείρω",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            "s1": "δῐᾰφθείρω",
            "s2": "δῐᾰφθείρεις",
            "s3": "δῐᾰφθείρει",
            "d2": "δῐᾰφθείρετον",
            "d3": "δῐᾰφθείρετον",
            "p1": "δῐᾰφθείρομεν",
            "p2": "δῐᾰφθείρετε",
            "p3": "δῐᾰφθείρουσῐ(ν)",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B1%E1%BC%B0%CF%83%CE%B8%CE%AC%CE%BD%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "αἰσθάνεσθαι",
    "passport": "αἰσθάνομαι",
    "dutch": "merken, gewaarworden",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "αἰσθάνομαι",
            "s2": ["αἰσθάνῃ", "αἰσθάνει"],
            "s3": "αἰσθάνεται",
            "d2": "αἰσθάνεσθον",
            "d3": "αἰσθάνεσθον",
            "p1": "αἰσθανόμεθᾰ",
            "p2": "αἰσθάνεσθε",
            "p3": "αἰσθάνονται"
          },
          "subjunctive": {
            "s1": "αἰσθάνωμαι",
            "s2": "αἰσθάνῃ",
            "s3": "αἰσθάνηται",
            "d2": "αἰσθάνησθον",
            "d3": "αἰσθάνησθον",
            "p1": "αἰσθανώμεθᾰ",
            "p2": "αἰσθάνησθε",
            "p3": "αἰσθάνωνται"
          },
          "optative": {
            "s1": "αἰσθανοίμην",
            "s2": "αἰσθάνοιο",
            "s3": "αἰσθάνοιτο",
            "d2": "αἰσθάνοισθον",
            "d3": "αἰσθανοίσθην",
            "p1": "αἰσθανοίμεθᾰ",
            "p2": "αἰσθάνοισθε",
            "p3": "αἰσθάνοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "αἰσθάνου",
            "s3": "αἰσθανέσθω",
            "d2": "αἰσθάνεσθον",
            "d3": "αἰσθανέσθων",
            "p1": "",
            "p2": "αἰσθάνεσθε",
            "p3": "αἰσθανέσθων"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "αἰσθάνεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "αἰσθανόμενος"
          },
          "f": {
            "active": "",
            "middle": "αἰσθανομένη"
          },
          "n": {
            "active": "",
            "middle": "αἰσθανόμενον"
          }
        }
      },
      "imperfect": {
        "middlePassive": {
          "indicative": {
            "s1": "ᾐσθανόμην",
            "s2": "ᾐσθάνου",
            "s3": "ᾐσθάνετο",
            "d2": "ᾐσθάνεσθον",
            "d3": "ᾐσθανέσθην",
            "p1": "ᾐσθανόμεθᾰ",
            "p2": "ᾐσθάνεσθε",
            "p3": "ᾐσθάνοντο"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CF%80%CE%BF%CE%BA%CF%81%CE%AF%CE%BD%CF%89
    "type": "verb",
    "ground": "ἀποκρίνεσθαι",
    "passport": "ἀπο-κρίνομαι",
    "dutch": "antwoorden",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓ποκρῑνω",
            "s2": "ᾰ̓ποκρῑνεις",
            "s3": "ᾰ̓ποκρῑνει",
            "d2": "ᾰ̓ποκρῑνετον",
            "d3": "ᾰ̓ποκρῑνετον",
            "p1": "ᾰ̓ποκρῑνομεν",
            "p2": "ᾰ̓ποκρῑνετε",
            "p3": "ᾰ̓ποκρῑνουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "ᾰ̓ποκρῑνω",
            "s2": "ᾰ̓ποκρῑνῃς",
            "s3": "ᾰ̓ποκρῑνῃ",
            "d2": "ᾰ̓ποκρῑνητον",
            "d3": "ᾰ̓ποκρῑνητον",
            "p1": "ᾰ̓ποκρῑνωμεν",
            "p2": "ᾰ̓ποκρῑνητε",
            "p3": "ᾰ̓ποκρῑνωσῐ(ν)"
          },
          "optative": {
            "s1": "ᾰ̓ποκρῑνοιμῐ",
            "s2": "ᾰ̓ποκρῑνοις",
            "s3": "ᾰ̓ποκρῑνοι",
            "d2": "ᾰ̓ποκρῑνοιτον",
            "d3": "ᾰ̓ποκρῑνοίτην",
            "p1": "ᾰ̓ποκρῑνοιμεν",
            "p2": "ᾰ̓ποκρῑνοιτε",
            "p3": "ᾰ̓ποκρῑνοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "ᾰ̓πόκρῑνε",
            "s3": "ᾰ̓ποκρῑνέτω",
            "d2": "ᾰ̓ποκρῑνετον",
            "d3": "ᾰ̓ποκρῑνέτων",
            "p1": "",
            "p2": "ᾰ̓ποκρῑνετε",
            "p3": "ᾰ̓ποκρῑνόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ᾰ̓ποκρῑνομαι",
            "s2": ["ᾰ̓ποκρῑνῃ", "ᾰ̓ποκρῑνει"],
            "s3": "ᾰ̓ποκρῑνεται",
            "d2": "ᾰ̓ποκρῑνεσθον",
            "d3": "ᾰ̓ποκρῑνεσθον",
            "p1": "ᾰ̓ποκρῑνόμεθᾰ",
            "p2": "ᾰ̓ποκρῑνεσθε",
            "p3": "ᾰ̓ποκρῑνονται"
          },
          "subjunctive": {
            "s1": "ᾰ̓ποκρῑνωμαι",
            "s2": "ᾰ̓ποκρῑνῃ",
            "s3": "ᾰ̓ποκρῑνηται",
            "d2": "ᾰ̓ποκρῑνησθον",
            "d3": "ᾰ̓ποκρῑνησθον",
            "p1": "ᾰ̓ποκρῑνώμεθᾰ",
            "p2": "ᾰ̓ποκρῑνησθε",
            "p3": "ᾰ̓ποκρῑνωνται"
          },
          "optative": {
            "s1": "ᾰ̓ποκρῑνοίμην",
            "s2": "ᾰ̓ποκρῑνοιο",
            "s3": "ᾰ̓ποκρῑνοιτο",
            "d2": "ᾰ̓ποκρῑνοισθον",
            "d3": "ᾰ̓ποκρῑνοίσθην",
            "p1": "ᾰ̓ποκρῑνοίμεθᾰ",
            "p2": "ᾰ̓ποκρῑνοισθε",
            "p3": "ᾰ̓ποκρῑνοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "ᾰ̓ποκρῑνου",
            "s3": "ᾰ̓ποκρῑνέσθω",
            "d2": "ᾰ̓ποκρῑνεσθον",
            "d3": "ᾰ̓ποκρῑνέσθων",
            "p1": "",
            "p2": "ᾰ̓ποκρῑνεσθε",
            "p3": "ᾰ̓ποκρῑνέσθων"
          }
        },
        "infinitive": {
          "active": "ᾰ̓ποκρῑνειν",
          "middle": "ᾰ̓ποκρῑνεσθαι"
        },
        "participle": {
          "m": {
            "active": "ᾰ̓ποκρῑνων",
            "middle": "ᾰ̓ποκρῑνόμενος"
          },
          "f": {
            "active": "ᾰ̓ποκρῑνουσᾰ",
            "middle": "ᾰ̓ποκρῑνομένη"
          },
          "n": {
            "active": "ᾰ̓ποκρῖνον",
            "middle": "ᾰ̓ποκρῑνόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓πέκρῑνον",
            "s2": "ᾰ̓πέκρῑνες",
            "s3": "ᾰ̓πέκρῑνε(ν)",
            "d2": "ᾰ̓πεκρῑνετον",
            "d3": "ᾰ̓πεκρῑνέτην",
            "p1": "ᾰ̓πεκρῑνομεν",
            "p2": "ᾰ̓πεκρῑνετε",
            "p3": "ᾰ̓πέκρῑνον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ᾰ̓πεκρῑνόμην",
            "s2": "ᾰ̓πεκρῑνου",
            "s3": "ᾰ̓πεκρῑνετο",
            "d2": "ᾰ̓πεκρῑνεσθον",
            "d3": "ᾰ̓πεκρῑνέσθην",
            "p1": "ᾰ̓πεκρῑνόμεθᾰ",
            "p2": "ᾰ̓πεκρῑνεσθε",
            "p3": "ᾰ̓πεκρῑνοντο"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B2%CE%BF%CF%8D%CE%BB%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "βούλεσθαι",
    "passport": "βούλομαι",
    "dutch": "willen",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "βούλομαι",
            "s2": "βούλῃ,βούλει",
            "s3": "βούλεται",
            "d2": "βούλεσθον",
            "d3": "βούλεσθον",
            "p1": "βουλόμεθᾰ",
            "p2": "βούλεσθε",
            "p3": "βούλονται"
          },
          "subjunctive": {
            "s1": "βούλωμαι",
            "s2": "βούλῃ",
            "s3": "βούληται",
            "d2": "βούλησθον",
            "d3": "βούλησθον",
            "p1": "βουλώμεθᾰ",
            "p2": "βούλησθε",
            "p3": "βούλωνται"
          },
          "optative": {
            "s1": "βουλοίμην",
            "s2": "βούλοιο",
            "s3": "βούλοιτο",
            "d2": "βούλοισθον",
            "d3": "βουλοίσθην",
            "p1": "βουλοίμεθᾰ",
            "p2": "βούλοισθε",
            "p3": "βούλοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "βούλου",
            "s3": "βουλέσθω",
            "d2": "βούλεσθον",
            "d3": "βουλέσθων",
            "p1": "",
            "p2": "βούλεσθε",
            "p3": "βουλέσθων"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "βούλεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "βουλόμενος"
          },
          "f": {
            "active": "",
            "middle": "βουλομένη"
          },
          "n": {
            "active": "",
            "middle": "βουλόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B3%CE%AF%CE%B3%CE%BD%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "γίγνεσθαι",
    "passport": "γίγνομαι",
    "dutch": "worden, ontstaan, gebeuren",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "γίγνομαι",
            "s2": "γίγνῃ,γίγνει",
            "s3": "γίγνεται",
            "d2": "γίγνεσθον",
            "d3": "γίγνεσθον",
            "p1": "γιγνόμεθᾰ",
            "p2": "γίγνεσθε",
            "p3": "γίγνονται"
          },
          "subjunctive": {
            "s1": "γίγνωμαι",
            "s2": "γίγνῃ",
            "s3": "γίγνηται",
            "d2": "γίγνησθον",
            "d3": "γίγνησθον",
            "p1": "γιγνώμεθᾰ",
            "p2": "γίγνησθε",
            "p3": "γίγνωνται"
          },
          "optative": {
            "s1": "γιγνοίμην",
            "s2": "γίγνοιο",
            "s3": "γίγνοιτο",
            "d2": "γίγνοισθον",
            "d3": "γιγνοίσθην",
            "p1": "γιγνοίμεθᾰ",
            "p2": "γίγνοισθε",
            "p3": "γίγνοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "γίγνου",
            "s3": "γιγνέσθω",
            "d2": "γίγνεσθον",
            "d3": "γιγνέσθων",
            "p1": "",
            "p2": "γίγνεσθε",
            "p3": "γιγνέσθων"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "γίγνεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "γιγνόμενος"
          },
          "f": {
            "active": "",
            "middle": "γιγνομένη"
          },
          "n": {
            "active": "",
            "middle": "γιγνόμενον"
          }
        }
      },
      "future": {
        "middle": {
          "indicative": {
            "s1": "γενήσομαι",
            "s2": "γενήσῃ,γενήσει",
            "s3": "γενήσεται",
            "d2": "γενήσεσθον",
            "d3": "γενήσεσθον",
            "p1": "γενησόμεθᾰ",
            "p2": "γενήσεσθε",
            "p3": "γενήσονται"
          },
          "optative": {
            "s1": "γενησοίμην",
            "s2": "γενήσοιο",
            "s3": "γενήσοιτο",
            "d2": "γενήσοισθον",
            "d3": "γενησοίσθην",
            "p1": "γενησοίμεθᾰ",
            "p2": "γενήσοισθε",
            "p3": "γενήσοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "γενηθήσομαι",
            "s2": "γενηθήσῃ",
            "s3": "γενηθήσεται",
            "d2": "γενηθήσεσθον",
            "d3": "γενηθήσεσθον",
            "p1": "γενηθησόμεθᾰ",
            "p2": "γενηθήσεσθε",
            "p3": "γενηθήσονται"
          },
          "optative": {
            "s1": "γενηθησοίμην",
            "s2": "γενηθήσοιο",
            "s3": "γενηθήσοιτο",
            "d2": "γενηθήσοισθον",
            "d3": "γενηθησοίσθην",
            "p1": "γενηθησοίμεθᾰ",
            "p2": "γενηθήσοισθε",
            "p3": "γενηθήσοιντο"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "γενήσεσθαι",
          "passive": "γενηθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "γενησόμενος",
            "passive": "γενηθησόμενος"
          },
          "f": {
            "active": "",
            "middle": "γενησομένη",
            "passive": "γενηθησομένη"
          },
          "n": {
            "active": "",
            "middle": "γενησόμενον",
            "passive": "γενηθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BC%CE%AC%CF%87%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "μᾰχεσθαι",
    "passport": "μᾰχομαι",
    "dutch": "strijden, vechten",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "μᾰχομαι",
            "s2": "μᾰχῃ,μᾰχει",
            "s3": "μᾰχεται",
            "d2": "μᾰχεσθον",
            "d3": "μᾰχεσθον",
            "p1": "μᾰχόμεθᾰ",
            "p2": "μᾰχεσθε",
            "p3": "μᾰχονται"
          },
          "subjunctive": {
            "s1": "μᾰχωμαι",
            "s2": "μᾰχῃ",
            "s3": "μᾰχηται",
            "d2": "μᾰχησθον",
            "d3": "μᾰχησθον",
            "p1": "μᾰχώμεθᾰ",
            "p2": "μᾰχησθε",
            "p3": "μᾰχωνται"
          },
          "optative": {
            "s1": "μᾰχοίμην",
            "s2": "μᾰχοιο",
            "s3": "μᾰχοιτο",
            "d2": "μᾰχοισθον",
            "d3": "μᾰχοίσθην",
            "p1": "μᾰχοίμεθᾰ",
            "p2": "μᾰχοισθε",
            "p3": "μᾰχοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "μᾰχου",
            "s3": "μᾰχέσθω",
            "d2": "μᾰχεσθον",
            "d3": "μᾰχέσθων",
            "p1": "",
            "p2": "μᾰχεσθε",
            "p3": "μᾰχέσθων"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "μᾰχεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "μᾰχόμενος"
          },
          "f": {
            "active": "",
            "middle": "μᾰχομένη"
          },
          "n": {
            "active": "",
            "middle": "μᾰχόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%B1%CF%8D%CF%89
    "type": "verb",
    "ground": "παύειν",
    "passport": "παύω",
    "dutch": "doen ophouden, doen stoppen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "παύω",
            "s2": "παύεις",
            "s3": "παύει",
            "d2": "παύετον",
            "d3": "παύετον",
            "p1": "παύομεν",
            "p2": "παύετε",
            "p3": "παύουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "παύω",
            "s2": "παύῃς",
            "s3": "παύῃ",
            "d2": "παύητον",
            "d3": "παύητον",
            "p1": "παύωμεν",
            "p2": "παύητε",
            "p3": "παύωσῐ(ν)"
          },
          "optative": {
            "s1": "παύοιμῐ",
            "s2": "παύοις",
            "s3": "παύοι",
            "d2": "παύοιτον",
            "d3": "παυοίτην",
            "p1": "παύοιμεν",
            "p2": "παύοιτε",
            "p3": "παύοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "παῦε",
            "s3": "παυέτω",
            "d2": "παύετον",
            "d3": "παυέτων",
            "p1": "",
            "p2": "παύετε",
            "p3": "παυόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "παύομαι",
            "s2": "παύῃ,παύει",
            "s3": "παύεται",
            "d2": "παύεσθον",
            "d3": "παύεσθον",
            "p1": "παυόμεθᾰ",
            "p2": "παύεσθε",
            "p3": "παύονται"
          },
          "subjunctive": {
            "s1": "παύωμαι",
            "s2": "παύῃ",
            "s3": "παύηται",
            "d2": "παύησθον",
            "d3": "παύησθον",
            "p1": "παυώμεθᾰ",
            "p2": "παύησθε",
            "p3": "παύωνται"
          },
          "optative": {
            "s1": "παυοίμην",
            "s2": "παύοιο",
            "s3": "παύοιτο",
            "d2": "παύοισθον",
            "d3": "παυοίσθην",
            "p1": "παυοίμεθᾰ",
            "p2": "παύοισθε",
            "p3": "παύοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "παύου",
            "s3": "παυέσθω",
            "d2": "παύεσθον",
            "d3": "παυέσθων",
            "p1": "",
            "p2": "παύεσθε",
            "p3": "παυέσθων"
          }
        },
        "infinitive": {
          "active": "παύειν",
          "middle": "παύεσθαι"
        },
        "participle": {
          "m": {
            "active": "παύων",
            "middle": "παυόμενος"
          },
          "f": {
            "active": "παύουσᾰ",
            "middle": "παυομένη"
          },
          "n": {
            "active": "παῦον",
            "middle": "παυόμενον"
          }
        }
      }
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%B5%CE%AF%CE%B8%CF%89
    "type": "verb",
    "ground": "πείθειν",
    "passport": "πείθω",
    "dutch": "overtuigen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "πείθω",
            "s2": "πείθεις",
            "s3": "πείθει",
            "d2": "πείθετον",
            "d3": "πείθετον",
            "p1": "πείθομεν",
            "p2": "πείθετε",
            "p3": "πείθουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "πείθω",
            "s2": "πείθῃς",
            "s3": "πείθῃ",
            "d2": "πείθητον",
            "d3": "πείθητον",
            "p1": "πείθωμεν",
            "p2": "πείθητε",
            "p3": "πείθωσῐ(ν)"
          },
          "optative": {
            "s1": "πείθοιμῐ",
            "s2": "πείθοις",
            "s3": "πείθοι",
            "d2": "πείθοιτον",
            "d3": "πειθοίτην",
            "p1": "πείθοιμεν",
            "p2": "πείθοιτε",
            "p3": "πείθοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "πεῖθε",
            "s3": "πειθέτω",
            "d2": "πείθετον",
            "d3": "πειθέτων",
            "p1": "",
            "p2": "πείθετε",
            "p3": "πειθόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "πείθομαι",
            "s2": "πείθῃ,πείθει",
            "s3": "πείθεται",
            "d2": "πείθεσθον",
            "d3": "πείθεσθον",
            "p1": "πειθόμεθᾰ",
            "p2": "πείθεσθε",
            "p3": "πείθονται"
          },
          "subjunctive": {
            "s1": "πείθωμαι",
            "s2": "πείθῃ",
            "s3": "πείθηται",
            "d2": "πείθησθον",
            "d3": "πείθησθον",
            "p1": "πειθώμεθᾰ",
            "p2": "πείθησθε",
            "p3": "πείθωνται"
          },
          "optative": {
            "s1": "πειθοίμην",
            "s2": "πείθοιο",
            "s3": "πείθοιτο",
            "d2": "πείθοισθον",
            "d3": "πειθοίσθην",
            "p1": "πειθοίμεθᾰ",
            "p2": "πείθοισθε",
            "p3": "πείθοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "πείθου",
            "s3": "πειθέσθω",
            "d2": "πείθεσθον",
            "d3": "πειθέσθων",
            "p1": "",
            "p2": "πείθεσθε",
            "p3": "πειθέσθων"
          }
        },
        "infinitive": {
          "active": "πείθειν",
          "middle": "πείθεσθαι"
        },
        "participle": {
          "m": {
            "active": "πείθων",
            "middle": "πειθόμενος"
          },
          "f": {
            "active": "πείθουσᾰ",
            "middle": "πειθομένη"
          },
          "n": {
            "active": "πεῖθον",
            "middle": "πειθόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CF%86%CE%B1%CE%AF%CE%BD%CF%89
    "type": "verb",
    "ground": "φαίνειν",
    "passport": "φαίνω",
    "dutch": "laten zien",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "φαίνω",
            "s2": "φαίνεις",
            "s3": "φαίνει",
            "d2": "φαίνετον",
            "d3": "φαίνετον",
            "p1": "φαίνομεν",
            "p2": "φαίνετε",
            "p3": "φαίνουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "φαίνω",
            "s2": "φαίνῃς",
            "s3": "φαίνῃ",
            "d2": "φαίνητον",
            "d3": "φαίνητον",
            "p1": "φαίνωμεν",
            "p2": "φαίνητε",
            "p3": "φαίνωσῐ(ν)"
          },
          "optative": {
            "s1": "φαίνοιμῐ",
            "s2": "φαίνοις",
            "s3": "φαίνοι",
            "d2": "φαίνοιτον",
            "d3": "φαινοίτην",
            "p1": "φαίνοιμεν",
            "p2": "φαίνοιτε",
            "p3": "φαίνοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "φαῖνε",
            "s3": "φαινέτω",
            "d2": "φαίνετον",
            "d3": "φαινέτων",
            "p1": "",
            "p2": "φαίνετε",
            "p3": "φαινόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "φαίνομαι",
            "s2": "φαίνῃ,φαίνει",
            "s3": "φαίνεται",
            "d2": "φαίνεσθον",
            "d3": "φαίνεσθον",
            "p1": "φαινόμεθᾰ",
            "p2": "φαίνεσθε",
            "p3": "φαίνονται"
          },
          "subjunctive": {
            "s1": "φαίνωμαι",
            "s2": "φαίνῃ",
            "s3": "φαίνηται",
            "d2": "φαίνησθον",
            "d3": "φαίνησθον",
            "p1": "φαινώμεθᾰ",
            "p2": "φαίνησθε",
            "p3": "φαίνωνται"
          },
          "optative": {
            "s1": "φαινοίμην",
            "s2": "φαίνοιο",
            "s3": "φαίνοιτο",
            "d2": "φαίνοισθον",
            "d3": "φαινοίσθην",
            "p1": "φαινοίμεθᾰ",
            "p2": "φαίνοισθε",
            "p3": "φαίνοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "φαίνου",
            "s3": "φαινέσθω",
            "d2": "φαίνεσθον",
            "d3": "φαινέσθων",
            "p1": "",
            "p2": "φαίνεσθε",
            "p3": "φαινέσθων"
          }
        },
        "infinitive": {
          "active": "φαίνειν",
          "middle": "φαίνεσθαι"
        },
        "participle": {
          "m": {
            "active": "φαίνων",
            "middle": "φαινόμενος"
          },
          "f": {
            "active": "φαίνουσᾰ",
            "middle": "φαινομένη"
          },
          "n": {
            "active": "φαῖνον",
            "middle": "φαινόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%94%CF%81%CF%87%CE%BF%CE%BC%CE%B1%CE%B9
    type: "verb",
    dutch: "gaan",
    ground: "ἰέναι",
    passport: "ἔρχομαι",
    explicitConjugations: {
      present: {
        active: {
          indicative: {
            s1: "ἔρχομαι",
            s2: ["ἔρχῃ", "ἔρχει"],
            s3: "ἔρχεται",
            d2: "ἔρχεσθον",
            d3: "ἔρχεσθον",
            p1: "ἐρχόμεθᾰ",
            p2: "ἔρχεσθε",
            p3: "ἔρχονται",
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%84%CF%81%CF%87%CF%89
    "type": "verb",
    "ground": "ᾰ̓ρχειν",
    "passport": "ᾰ̓ρχω",
    "dutch": "heersen (over)",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓ρχω",
            "s2": "ᾰ̓ρχεις",
            "s3": "ᾰ̓ρχει",
            "d2": "ᾰ̓ρχετον",
            "d3": "ᾰ̓ρχετον",
            "p1": "ᾰ̓ρχομεν",
            "p2": "ᾰ̓ρχετε",
            "p3": "ᾰ̓ρχουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "ᾰ̓ρχω",
            "s2": "ᾰ̓ρχῃς",
            "s3": "ᾰ̓ρχῃ",
            "d2": "ᾰ̓ρχητον",
            "d3": "ᾰ̓ρχητον",
            "p1": "ᾰ̓ρχωμεν",
            "p2": "ᾰ̓ρχητε",
            "p3": "ᾰ̓ρχωσῐ(ν)"
          },
          "optative": {
            "s1": "ᾰ̓ρχοιμῐ",
            "s2": "ᾰ̓ρχοις",
            "s3": "ᾰ̓ρχοι",
            "d2": "ᾰ̓ρχοιτον",
            "d3": "ᾰ̓ρχοίτην",
            "p1": "ᾰ̓ρχοιμεν",
            "p2": "ᾰ̓ρχοιτε",
            "p3": "ᾰ̓ρχοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "ᾰ̓ρχε",
            "s3": "ᾰ̓ρχέτω",
            "d2": "ᾰ̓ρχετον",
            "d3": "ᾰ̓ρχέτων",
            "p1": "",
            "p2": "ᾰ̓ρχετε",
            "p3": "ᾰ̓ρχόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ᾰ̓ρχομαι",
            "s2": "ᾰ̓ρχῃ,ᾰ̓ρχει",
            "s3": "ᾰ̓ρχεται",
            "d2": "ᾰ̓ρχεσθον",
            "d3": "ᾰ̓ρχεσθον",
            "p1": "ᾰ̓ρχόμεθᾰ",
            "p2": "ᾰ̓ρχεσθε",
            "p3": "ᾰ̓ρχονται"
          },
          "subjunctive": {
            "s1": "ᾰ̓ρχωμαι",
            "s2": "ᾰ̓ρχῃ",
            "s3": "ᾰ̓ρχηται",
            "d2": "ᾰ̓ρχησθον",
            "d3": "ᾰ̓ρχησθον",
            "p1": "ᾰ̓ρχώμεθᾰ",
            "p2": "ᾰ̓ρχησθε",
            "p3": "ᾰ̓ρχωνται"
          },
          "optative": {
            "s1": "ᾰ̓ρχοίμην",
            "s2": "ᾰ̓ρχοιο",
            "s3": "ᾰ̓ρχοιτο",
            "d2": "ᾰ̓ρχοισθον",
            "d3": "ᾰ̓ρχοίσθην",
            "p1": "ᾰ̓ρχοίμεθᾰ",
            "p2": "ᾰ̓ρχοισθε",
            "p3": "ᾰ̓ρχοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "ᾰ̓ρχου",
            "s3": "ᾰ̓ρχέσθω",
            "d2": "ᾰ̓ρχεσθον",
            "d3": "ᾰ̓ρχέσθων",
            "p1": "",
            "p2": "ᾰ̓ρχεσθε",
            "p3": "ᾰ̓ρχέσθων"
          }
        },
        "infinitive": {
          "active": "ᾰ̓ρχειν",
          "middle": "ᾰ̓ρχεσθαι"
        },
        "participle": {
          "m": {
            "active": "ᾰ̓ρχων",
            "middle": "ᾰ̓ρχόμενος"
          },
          "f": {
            "active": "ᾰ̓ρχουσᾰ",
            "middle": "ᾰ̓ρχομένη"
          },
          "n": {
            "active": "ᾰ̓ρχον",
            "middle": "ᾰ̓ρχόμενον"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓ρξω",
            "s2": "ᾰ̓ρξεις",
            "s3": "ᾰ̓ρξει",
            "d2": "ᾰ̓ρξετον",
            "d3": "ᾰ̓ρξετον",
            "p1": "ᾰ̓ρξομεν",
            "p2": "ᾰ̓ρξετε",
            "p3": "ᾰ̓ρξουσῐ(ν)"
          },
          "optative": {
            "s1": "ᾰ̓ρξοιμῐ",
            "s2": "ᾰ̓ρξοις",
            "s3": "ᾰ̓ρξοι",
            "d2": "ᾰ̓ρξοιτον",
            "d3": "ᾰ̓ρξοίτην",
            "p1": "ᾰ̓ρξοιμεν",
            "p2": "ᾰ̓ρξοιτε",
            "p3": "ᾰ̓ρξοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "ᾰ̓ρξομαι",
            "s2": "ᾰ̓ρξῃ,ᾰ̓ρξει",
            "s3": "ᾰ̓ρξεται",
            "d2": "ᾰ̓ρξεσθον",
            "d3": "ᾰ̓ρξεσθον",
            "p1": "ᾰ̓ρξόμεθᾰ",
            "p2": "ᾰ̓ρξεσθε",
            "p3": "ᾰ̓ρξονται"
          },
          "optative": {
            "s1": "ᾰ̓ρξοίμην",
            "s2": "ᾰ̓ρξοιο",
            "s3": "ᾰ̓ρξοιτο",
            "d2": "ᾰ̓ρξοισθον",
            "d3": "ᾰ̓ρξοίσθην",
            "p1": "ᾰ̓ρξοίμεθᾰ",
            "p2": "ᾰ̓ρξοισθε",
            "p3": "ᾰ̓ρξοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "ᾰ̓ρχθήσομαι",
            "s2": "ᾰ̓ρχθήσῃ",
            "s3": "ᾰ̓ρχθήσεται",
            "d2": "ᾰ̓ρχθήσεσθον",
            "d3": "ᾰ̓ρχθήσεσθον",
            "p1": "ᾰ̓ρχθησόμεθᾰ",
            "p2": "ᾰ̓ρχθήσεσθε",
            "p3": "ᾰ̓ρχθήσονται"
          },
          "optative": {
            "s1": "ᾰ̓ρχθησοίμην",
            "s2": "ᾰ̓ρχθήσοιο",
            "s3": "ᾰ̓ρχθήσοιτο",
            "d2": "ᾰ̓ρχθήσοισθον",
            "d3": "ᾰ̓ρχθησοίσθην",
            "p1": "ᾰ̓ρχθησοίμεθᾰ",
            "p2": "ᾰ̓ρχθήσοισθε",
            "p3": "ᾰ̓ρχθήσοιντο"
          }
        },
        "infinitive": {
          "active": "ᾰ̓ρξειν",
          "middle": "ᾰ̓ρξεσθαι",
          "passive": "ᾰ̓ρχθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "ᾰ̓ρξων",
            "middle": "ᾰ̓ρξόμενος",
            "passive": "ᾰ̓ρχθησόμενος"
          },
          "f": {
            "active": "ᾰ̓ρξουσᾰ",
            "middle": "ᾰ̓ρξομένη",
            "passive": "ᾰ̓ρχθησομένη"
          },
          "n": {
            "active": "ᾰ̓ρξον",
            "middle": "ᾰ̓ρξόμενον",
            "passive": "ᾰ̓ρχθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B2%CE%BF%CF%85%CE%BB%CE%B5%CF%8D%CF%89
    "type": "verb",
    "ground": "βουλεύειν",
    "passport": "βουλεύω",
    "dutch": "beraadslagen, besluiten",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "βουλεύω",
            "s2": "βουλεύεις",
            "s3": "βουλεύει",
            "d2": "βουλεύετον",
            "d3": "βουλεύετον",
            "p1": "βουλεύομεν",
            "p2": "βουλεύετε",
            "p3": "βουλεύουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "βουλεύω",
            "s2": "βουλεύῃς",
            "s3": "βουλεύῃ",
            "d2": "βουλεύητον",
            "d3": "βουλεύητον",
            "p1": "βουλεύωμεν",
            "p2": "βουλεύητε",
            "p3": "βουλεύωσῐ(ν)"
          },
          "optative": {
            "s1": "βουλεύοιμῐ",
            "s2": "βουλεύοις",
            "s3": "βουλεύοι",
            "d2": "βουλεύοιτον",
            "d3": "βουλευοίτην",
            "p1": "βουλεύοιμεν",
            "p2": "βουλεύοιτε",
            "p3": "βουλεύοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "βούλευε",
            "s3": "βουλευέτω",
            "d2": "βουλεύετον",
            "d3": "βουλευέτων",
            "p1": "",
            "p2": "βουλεύετε",
            "p3": "βουλευόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "βουλεύομαι",
            "s2": "βουλεύῃ,βουλεύει",
            "s3": "βουλεύεται",
            "d2": "βουλεύεσθον",
            "d3": "βουλεύεσθον",
            "p1": "βουλευόμεθᾰ",
            "p2": "βουλεύεσθε",
            "p3": "βουλεύονται"
          },
          "subjunctive": {
            "s1": "βουλεύωμαι",
            "s2": "βουλεύῃ",
            "s3": "βουλεύηται",
            "d2": "βουλεύησθον",
            "d3": "βουλεύησθον",
            "p1": "βουλευώμεθᾰ",
            "p2": "βουλεύησθε",
            "p3": "βουλεύωνται"
          },
          "optative": {
            "s1": "βουλευοίμην",
            "s2": "βουλεύοιο",
            "s3": "βουλεύοιτο",
            "d2": "βουλεύοισθον",
            "d3": "βουλευοίσθην",
            "p1": "βουλευοίμεθᾰ",
            "p2": "βουλεύοισθε",
            "p3": "βουλεύοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "βουλεύου",
            "s3": "βουλευέσθω",
            "d2": "βουλεύεσθον",
            "d3": "βουλευέσθων",
            "p1": "",
            "p2": "βουλεύεσθε",
            "p3": "βουλευέσθων"
          }
        },
        "infinitive": {
          "active": "βουλεύειν",
          "middle": "βουλεύεσθαι"
        },
        "participle": {
          "m": {
            "active": "βουλεύων",
            "middle": "βουλευόμενος"
          },
          "f": {
            "active": "βουλεύουσᾰ",
            "middle": "βουλευομένη"
          },
          "n": {
            "active": "βουλεῦον",
            "middle": "βουλευόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἐβούλευον",
            "s2": "ἐβούλευες",
            "s3": "ἐβούλευε(ν)",
            "d2": "ἐβουλεύετον",
            "d3": "ἐβουλευέτην",
            "p1": "ἐβουλεύομεν",
            "p2": "ἐβουλεύετε",
            "p3": "ἐβούλευον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἐβουλευόμην",
            "s2": "ἐβουλεύου",
            "s3": "ἐβουλεύετο",
            "d2": "ἐβουλεύεσθον",
            "d3": "ἐβουλευέσθην",
            "p1": "ἐβουλευόμεθᾰ",
            "p2": "ἐβουλεύεσθε",
            "p3": "ἐβουλεύοντο"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CE%AD%CF%87%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "δέχεσθαι",
    "passport": "δέχομαι",
    "dutch": "ontvangen",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "δέχομαι",
            "s2": ["δέχῃ", "δέχει"],
            "s3": "δέχεται",
            "d2": "δέχεσθον",
            "d3": "δέχεσθον",
            "p1": "δεχόμεθᾰ",
            "p2": "δέχεσθε",
            "p3": "δέχονται"
          },
          "subjunctive": {
            "s1": "δέχωμαι",
            "s2": "δέχῃ",
            "s3": "δέχηται",
            "d2": "δέχησθον",
            "d3": "δέχησθον",
            "p1": "δεχώμεθᾰ",
            "p2": "δέχησθε",
            "p3": "δέχωνται"
          },
          "optative": {
            "s1": "δεχοίμην",
            "s2": "δέχοιο",
            "s3": "δέχοιτο",
            "d2": "δέχοισθον",
            "d3": "δεχοίσθην",
            "p1": "δεχοίμεθᾰ",
            "p2": "δέχοισθε",
            "p3": "δέχοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "δέχου",
            "s3": "δεχέσθω",
            "d2": "δέχεσθον",
            "d3": "δεχέσθων",
            "p1": "",
            "p2": "δέχεσθε",
            "p3": "δεχέσθων"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "δέχεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "δεχόμενος"
          },
          "f": {
            "active": "",
            "middle": "δεχομένη"
          },
          "n": {
            "active": "",
            "middle": "δεχόμενον"
          }
        }
      },
      "imperfect": {
        "middlePassive": {
          "indicative": {
            "s1": "ἐδεχόμην",
            "s2": "ἐδέχου",
            "s3": "ἐδέχετο",
            "d2": "ἐδέχεσθον",
            "d3": "ἐδεχέσθην",
            "p1": "ἐδεχόμεθᾰ",
            "p2": "ἐδέχεσθε",
            "p3": "ἐδέχοντο"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%90%CF%81%CE%B3%CE%AC%CE%B6%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "ἐργᾰζεσθαι",
    "passport": "ἐργᾰζομαι",
    "dutch": "werken",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "ἐργᾰζομαι",
            "s2": ["ἐργᾰζῃ", "ἐργᾰζει"],
            "s3": "ἐργᾰζεται",
            "d2": "ἐργᾰζεσθον",
            "d3": "ἐργᾰζεσθον",
            "p1": "ἐργᾰζόμεθᾰ",
            "p2": "ἐργᾰζεσθε",
            "p3": "ἐργᾰζονται"
          },
          "subjunctive": {
            "s1": "ἐργᾰζωμαι",
            "s2": "ἐργᾰζῃ",
            "s3": "ἐργᾰζηται",
            "d2": "ἐργᾰζησθον",
            "d3": "ἐργᾰζησθον",
            "p1": "ἐργᾰζώμεθᾰ",
            "p2": "ἐργᾰζησθε",
            "p3": "ἐργᾰζωνται"
          },
          "optative": {
            "s1": "ἐργᾰζοίμην",
            "s2": "ἐργᾰζοιο",
            "s3": "ἐργᾰζοιτο",
            "d2": "ἐργᾰζοισθον",
            "d3": "ἐργᾰζοίσθην",
            "p1": "ἐργᾰζοίμεθᾰ",
            "p2": "ἐργᾰζοισθε",
            "p3": "ἐργᾰζοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "ἐργᾰζου",
            "s3": "ἐργᾰζέσθω",
            "d2": "ἐργᾰζεσθον",
            "d3": "ἐργᾰζέσθων",
            "p1": "",
            "p2": "ἐργᾰζεσθε",
            "p3": "ἐργᾰζέσθων"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "ἐργᾰζεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "ἐργᾰζόμενος"
          },
          "f": {
            "active": "",
            "middle": "ἐργᾰζομένη"
          },
          "n": {
            "active": "",
            "middle": "ἐργᾰζόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BF%E1%BC%B4%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "οἴεσθαι",
    "passport": "οἴομαι",
    "dutch": "denken, menen",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "οἴομαι",
            "s2": ["οἴῃ", "οἴει"],
            "s3": "οἴεται",
            "d2": "οἴεσθον",
            "d3": "οἴεσθον",
            "p1": "οἰόμεθᾰ",
            "p2": "οἴεσθε",
            "p3": "οἴονται",
          },
        },
        "infinitive": {
          "middle": "οἴεσθαι",
        },
      },
      "imperfect": {
        "middlePassive": {
          "indicative": {
            "s1": "ᾠόμην",
            "s2": "ᾤου",
            "s3": "ᾤετο",
            "d2": "ᾤεσθον",
            "d3": "ᾠέσθην",
            "p1": "ᾠόμεθᾰ",
            "p2": "ᾤεσθε",
            "p3": "ᾤοντο"
          }
        }
      },
      "future": {
        "middle": {
          "indicative": {
            "s1": "οἰήσομαι",
            "s2": "οἰήσῃ,οἰήσει",
            "s3": "οἰήσεται",
            "d2": "οἰήσεσθον",
            "d3": "οἰήσεσθον",
            "p1": "οἰησόμεθᾰ",
            "p2": "οἰήσεσθε",
            "p3": "οἰήσονται"
          },
          "optative": {
            "s1": "οἰησοίμην",
            "s2": "οἰήσοιο",
            "s3": "οἰήσοιτο",
            "d2": "οἰήσοισθον",
            "d3": "οἰησοίσθην",
            "p1": "οἰησοίμεθᾰ",
            "p2": "οἰήσοισθε",
            "p3": "οἰήσοιντο"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "οἰήσεσθαι",
          "passive": ""
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "οἰησόμενος",
            "passive": ""
          },
          "f": {
            "active": "",
            "middle": "οἰησομένη",
            "passive": ""
          },
          "n": {
            "active": "",
            "middle": "οἰησόμενον",
            "passive": ""
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%BF%CF%81%CE%B5%CF%8D%CF%89
    "type": "verb",
    "ground": "πορεύειν",
    "passport": "πορεύω",
    "dutch": "reizen, oprukken",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "πορεύω",
            "s2": "πορεύεις",
            "s3": "πορεύει",
            "d2": "πορεύετον",
            "d3": "πορεύετον",
            "p1": "πορεύομεν",
            "p2": "πορεύετε",
            "p3": "πορεύουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "πορεύω",
            "s2": "πορεύῃς",
            "s3": "πορεύῃ",
            "d2": "πορεύητον",
            "d3": "πορεύητον",
            "p1": "πορεύωμεν",
            "p2": "πορεύητε",
            "p3": "πορεύωσῐ(ν)"
          },
          "optative": {
            "s1": "πορεύοιμῐ",
            "s2": "πορεύοις",
            "s3": "πορεύοι",
            "d2": "πορεύοιτον",
            "d3": "πορευοίτην",
            "p1": "πορεύοιμεν",
            "p2": "πορεύοιτε",
            "p3": "πορεύοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "πόρευε",
            "s3": "πορευέτω",
            "d2": "πορεύετον",
            "d3": "πορευέτων",
            "p1": "",
            "p2": "πορεύετε",
            "p3": "πορευόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "πορεύομαι",
            "s2": "πορεύῃ,πορεύει",
            "s3": "πορεύεται",
            "d2": "πορεύεσθον",
            "d3": "πορεύεσθον",
            "p1": "πορευόμεθᾰ",
            "p2": "πορεύεσθε",
            "p3": "πορεύονται"
          },
          "subjunctive": {
            "s1": "πορεύωμαι",
            "s2": "πορεύῃ",
            "s3": "πορεύηται",
            "d2": "πορεύησθον",
            "d3": "πορεύησθον",
            "p1": "πορευώμεθᾰ",
            "p2": "πορεύησθε",
            "p3": "πορεύωνται"
          },
          "optative": {
            "s1": "πορευοίμην",
            "s2": "πορεύοιο",
            "s3": "πορεύοιτο",
            "d2": "πορεύοισθον",
            "d3": "πορευοίσθην",
            "p1": "πορευοίμεθᾰ",
            "p2": "πορεύοισθε",
            "p3": "πορεύοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "πορεύου",
            "s3": "πορευέσθω",
            "d2": "πορεύεσθον",
            "d3": "πορευέσθων",
            "p1": "",
            "p2": "πορεύεσθε",
            "p3": "πορευέσθων"
          }
        },
        "infinitive": {
          "active": "πορεύειν",
          "middle": "πορεύεσθαι"
        },
        "participle": {
          "m": {
            "active": "πορεύων",
            "middle": "πορευόμενος"
          },
          "f": {
            "active": "πορεύουσᾰ",
            "middle": "πορευομένη"
          },
          "n": {
            "active": "πορεῦον",
            "middle": "πορευόμενον"
          }
        }
      },
      "imperfect": {
        "middlePassive": {
          "indicative": {
            "s1": "ἐπορευόμην",
            "s2": "ἐπορεύου",
            "s3": "ἐπορεύετο",
            "d2": "ἐπορεύεσθον",
            "d3": "ἐπορευέσθην",
            "p1": "ἐπορευόμεθᾰ",
            "p2": "ἐπορεύεσθε",
            "p3": "ἐπορεύοντο"
          }
        }
      },
      "future": {
        "middle": {
          "indicative": {
            "s1": "πορεύσομαι",
            "s2": "πορεύσῃ,πορεύσει",
            "s3": "πορεύσεται",
            "d2": "πορεύσεσθον",
            "d3": "πορεύσεσθον",
            "p1": "πορευσόμεθᾰ",
            "p2": "πορεύσεσθε",
            "p3": "πορεύσονται"
          },
          "optative": {
            "s1": "πορευσοίμην",
            "s2": "πορεύσοιο",
            "s3": "πορεύσοιτο",
            "d2": "πορεύσοισθον",
            "d3": "πορευσοίσθην",
            "p1": "πορευσοίμεθᾰ",
            "p2": "πορεύσοισθε",
            "p3": "πορεύσοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "πορευθήσομαι",
            "s2": "πορευθήσῃ",
            "s3": "πορευθήσεται",
            "d2": "πορευθήσεσθον",
            "d3": "πορευθήσεσθον",
            "p1": "πορευθησόμεθᾰ",
            "p2": "πορευθήσεσθε",
            "p3": "πορευθήσονται"
          },
          "optative": {
            "s1": "πορευθησοίμην",
            "s2": "πορευθήσοιο",
            "s3": "πορευθήσοιτο",
            "d2": "πορευθήσοισθον",
            "d3": "πορευθησοίσθην",
            "p1": "πορευθησοίμεθᾰ",
            "p2": "πορευθήσοισθε",
            "p3": "πορευθήσοιντο"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "πορεύσεσθαι",
          "passive": "πορευθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "πορευσόμενος",
            "passive": "πορευθησόμενος"
          },
          "f": {
            "active": "",
            "middle": "πορευσομένη",
            "passive": "πορευθησομένη"
          },
          "n": {
            "active": "",
            "middle": "πορευσόμενον",
            "passive": "πορευθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BA%CF%81%CE%AF%CE%BD%CF%89
    "type": "verb",
    "ground": "κρῑνειν",
    "passport": "κρῑνω",
    "dutch": "onderscheiden, oordelen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "κρῑνω",
            "s2": "κρῑνεις",
            "s3": "κρῑνει",
            "d2": "κρῑνετον",
            "d3": "κρῑνετον",
            "p1": "κρῑνομεν",
            "p2": "κρῑνετε",
            "p3": "κρῑνουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "κρῑνω",
            "s2": "κρῑνῃς",
            "s3": "κρῑνῃ",
            "d2": "κρῑνητον",
            "d3": "κρῑνητον",
            "p1": "κρῑνωμεν",
            "p2": "κρῑνητε",
            "p3": "κρῑνωσῐ(ν)"
          },
          "optative": {
            "s1": "κρῑνοιμῐ",
            "s2": "κρῑνοις",
            "s3": "κρῑνοι",
            "d2": "κρῑνοιτον",
            "d3": "κρῑνοίτην",
            "p1": "κρῑνοιμεν",
            "p2": "κρῑνοιτε",
            "p3": "κρῑνοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "κρῖνε",
            "s3": "κρῑνέτω",
            "d2": "κρῑνετον",
            "d3": "κρῑνέτων",
            "p1": "",
            "p2": "κρῑνετε",
            "p3": "κρῑνόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "κρῑνομαι",
            "s2": "κρῑνῃ,κρῑνει",
            "s3": "κρῑνεται",
            "d2": "κρῑνεσθον",
            "d3": "κρῑνεσθον",
            "p1": "κρῑνόμεθᾰ",
            "p2": "κρῑνεσθε",
            "p3": "κρῑνονται"
          },
          "subjunctive": {
            "s1": "κρῑνωμαι",
            "s2": "κρῑνῃ",
            "s3": "κρῑνηται",
            "d2": "κρῑνησθον",
            "d3": "κρῑνησθον",
            "p1": "κρῑνώμεθᾰ",
            "p2": "κρῑνησθε",
            "p3": "κρῑνωνται"
          },
          "optative": {
            "s1": "κρῑνοίμην",
            "s2": "κρῑνοιο",
            "s3": "κρῑνοιτο",
            "d2": "κρῑνοισθον",
            "d3": "κρῑνοίσθην",
            "p1": "κρῑνοίμεθᾰ",
            "p2": "κρῑνοισθε",
            "p3": "κρῑνοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "κρῑνου",
            "s3": "κρῑνέσθω",
            "d2": "κρῑνεσθον",
            "d3": "κρῑνέσθων",
            "p1": "",
            "p2": "κρῑνεσθε",
            "p3": "κρῑνέσθων"
          }
        },
        "infinitive": {
          "active": "κρῑνειν",
          "middle": "κρῑνεσθαι"
        },
        "participle": {
          "m": {
            "active": "κρῑνων",
            "middle": "κρῑνόμενος"
          },
          "f": {
            "active": "κρῑνουσᾰ",
            "middle": "κρῑνομένη"
          },
          "n": {
            "active": "κρῖνον",
            "middle": "κρῑνόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἔκρῑνον",
            "s2": "ἔκρῑνες",
            "s3": "ἔκρῑνε(ν)",
            "d2": "ἐκρῑνετον",
            "d3": "ἐκρῑνέτην",
            "p1": "ἐκρῑνομεν",
            "p2": "ἐκρῑνετε",
            "p3": "ἔκρῑνον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἐκρῑνόμην",
            "s2": "ἐκρῑνου",
            "s3": "ἐκρῑνετο",
            "d2": "ἐκρῑνεσθον",
            "d3": "ἐκρῑνέσθην",
            "p1": "ἐκρῑνόμεθᾰ",
            "p2": "ἐκρῑνεσθε",
            "p3": "ἐκρῑνοντο"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BC%CE%AD%CE%BB%CE%BB%CF%89
    "type": "verb",
    "ground": "μέλλειν",
    "passport": "μέλλω",
    "dutch": "dralen, van plan zijn (te)",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "μέλλω",
            "s2": "μέλλεις",
            "s3": "μέλλει",
            "d2": "μέλλετον",
            "d3": "μέλλετον",
            "p1": "μέλλομεν",
            "p2": "μέλλετε",
            "p3": "μέλλουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "μέλλω",
            "s2": "μέλλῃς",
            "s3": "μέλλῃ",
            "d2": "μέλλητον",
            "d3": "μέλλητον",
            "p1": "μέλλωμεν",
            "p2": "μέλλητε",
            "p3": "μέλλωσῐ(ν)"
          },
          "optative": {
            "s1": "μέλλοιμῐ",
            "s2": "μέλλοις",
            "s3": "μέλλοι",
            "d2": "μέλλοιτον",
            "d3": "μελλοίτην",
            "p1": "μέλλοιμεν",
            "p2": "μέλλοιτε",
            "p3": "μέλλοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "μέλλε",
            "s3": "μελλέτω",
            "d2": "μέλλετον",
            "d3": "μελλέτων",
            "p1": "",
            "p2": "μέλλετε",
            "p3": "μελλόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "μέλλομαι",
            "s2": "μέλλῃ,μέλλει",
            "s3": "μέλλεται",
            "d2": "μέλλεσθον",
            "d3": "μέλλεσθον",
            "p1": "μελλόμεθᾰ",
            "p2": "μέλλεσθε",
            "p3": "μέλλονται"
          },
          "subjunctive": {
            "s1": "μέλλωμαι",
            "s2": "μέλλῃ",
            "s3": "μέλληται",
            "d2": "μέλλησθον",
            "d3": "μέλλησθον",
            "p1": "μελλώμεθᾰ",
            "p2": "μέλλησθε",
            "p3": "μέλλωνται"
          },
          "optative": {
            "s1": "μελλοίμην",
            "s2": "μέλλοιο",
            "s3": "μέλλοιτο",
            "d2": "μέλλοισθον",
            "d3": "μελλοίσθην",
            "p1": "μελλοίμεθᾰ",
            "p2": "μέλλοισθε",
            "p3": "μέλλοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "μέλλου",
            "s3": "μελλέσθω",
            "d2": "μέλλεσθον",
            "d3": "μελλέσθων",
            "p1": "",
            "p2": "μέλλεσθε",
            "p3": "μελλέσθων"
          }
        },
        "infinitive": {
          "active": "μέλλειν",
          "middle": "μέλλεσθαι"
        },
        "participle": {
          "m": {
            "active": "μέλλων",
            "middle": "μελλόμενος"
          },
          "f": {
            "active": "μέλλουσᾰ",
            "middle": "μελλομένη"
          },
          "n": {
            "active": "μέλλον",
            "middle": "μελλόμενον"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "μελλήσω",
            "s2": "μελλήσεις",
            "s3": "μελλήσει",
            "d2": "μελλήσετον",
            "d3": "μελλήσετον",
            "p1": "μελλήσομεν",
            "p2": "μελλήσετε",
            "p3": "μελλήσουσῐ(ν)"
          },
          "optative": {
            "s1": "μελλήσοιμῐ",
            "s2": "μελλήσοις",
            "s3": "μελλήσοι",
            "d2": "μελλήσοιτον",
            "d3": "μελλησοίτην",
            "p1": "μελλήσοιμεν",
            "p2": "μελλήσοιτε",
            "p3": "μελλήσοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "μελλήσομαι",
            "s2": "μελλήσῃ,μελλήσει",
            "s3": "μελλήσεται",
            "d2": "μελλήσεσθον",
            "d3": "μελλήσεσθον",
            "p1": "μελλησόμεθᾰ",
            "p2": "μελλήσεσθε",
            "p3": "μελλήσονται"
          },
          "optative": {
            "s1": "μελλησοίμην",
            "s2": "μελλήσοιο",
            "s3": "μελλήσοιτο",
            "d2": "μελλήσοισθον",
            "d3": "μελλησοίσθην",
            "p1": "μελλησοίμεθᾰ",
            "p2": "μελλήσοισθε",
            "p3": "μελλήσοιντο"
          }
        },
        "infinitive": {
          "active": "μελλήσειν",
          "middle": "μελλήσεσθαι",
          "passive": ""
        },
        "participle": {
          "m": {
            "active": "μελλήσων",
            "middle": "μελλησόμενος",
            "passive": ""
          },
          "f": {
            "active": "μελλήσουσᾰ",
            "middle": "μελλησομένη",
            "passive": ""
          },
          "n": {
            "active": "μελλῆσον",
            "middle": "μελλησόμενον",
            "passive": ""
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CF%86%CE%B7%CE%BC%CE%AF
    "type": "verb",
    "ground": "φᾰναι",
    "passport": "φημῐ",
    "dutch": "zeggen, beweren",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "φημῐ",
            "s2": "φῄς",
            "s3": "φησῐ/φησῐν",
            "d2": "φᾰτόν",
            "d3": "φᾰτόν",
            "p1": "φᾰμέν",
            "p2": "φᾰτέ",
            "p3": "φᾱσῐ/φᾱσῐν"
          },
          "subjunctive": {
            "s1": "φῶ",
            "s2": "φῇς",
            "s3": "φῇ",
            "d2": "φῆτον",
            "d3": "φῆτον",
            "p1": "φῶμεν",
            "p2": "φῆτε",
            "p3": "φῶσῐ(ν)"
          },
          "optative": {
            "s1": "φαίην",
            "s2": "φαίης",
            "s3": "φαίη",
            "d2": "φαῖτον,φαίητον",
            "d3": "φαίτην,φαιήτην",
            "p1": "φαῖμεν,φαίημεν",
            "p2": "φαῖτε,φαίητε",
            "p3": "φαῖεν,φαίησᾰν"
          },
          "imperative": {
            "s1": "",
            "s2": "φᾰθῐ/φᾰθῐ",
            "s3": "φᾰτω",
            "d2": "φᾰτον",
            "d3": "φᾰτων",
            "p1": "",
            "p2": "φᾰτε",
            "p3": "φᾰντων"
          }
        },
        "infinitive": {
          "active": "φᾰναι",
          "middle": ""
        },
        "participle": {
          "m": {
            "active": "φᾱς",
            "middle": ""
          },
          "f": {
            "active": "φᾶσᾰ",
            "middle": ""
          },
          "n": {
            "active": "φᾰν",
            "middle": ""
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἔφην",
            "s2": "ἔφης/ἔφησθᾰ",
            "s3": "ἔφη(ν)",
            "d2": "ἔφᾰτον",
            "d3": "ἐφᾰτην",
            "p1": "ἔφᾰμεν",
            "p2": "ἔφᾰτε",
            "p3": "ἔφᾰσᾰν/ἔφᾰν"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "φήσω",
            "s2": "φήσεις",
            "s3": "φήσει",
            "d2": "φήσετον",
            "d3": "φήσετον",
            "p1": "φήσομεν",
            "p2": "φήσετε",
            "p3": "φήσουσῐ(ν)"
          },
          "optative": {
            "s1": "φήσοιμῐ",
            "s2": "φήσοις",
            "s3": "φήσοι",
            "d2": "φήσοιτον",
            "d3": "φησοίτην",
            "p1": "φήσοιμεν",
            "p2": "φήσοιτε",
            "p3": "φήσοιεν"
          }
        },
        "infinitive": {
          "active": "φήσειν",
          "middle": "",
          "passive": ""
        },
        "participle": {
          "m": {
            "active": "φήσων",
            "middle": "",
            "passive": ""
          },
          "f": {
            "active": "φήσουσᾰ",
            "middle": "",
            "passive": ""
          },
          "n": {
            "active": "φῆσον",
            "middle": "",
            "passive": ""
          }
        }
      }
    }
  },
  {
   // https://en.wiktionary.org/wiki/%E1%BC%95%CF%80%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "ἕπεσθαι",
    "passport": "ἕπομαι",
    "dutch": "volgen",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "ἕπομαι",
            "s2": "ἕπῃ,ἕπει",
            "s3": "ἕπεται",
            "d2": "ἕπεσθον",
            "d3": "ἕπεσθον",
            "p1": "ἑπόμεθᾰ",
            "p2": "ἕπεσθε",
            "p3": "ἕπονται"
          },
          "subjunctive": {
            "s1": "ἕπωμαι",
            "s2": "ἕπῃ",
            "s3": "ἕπηται",
            "d2": "ἕπησθον",
            "d3": "ἕπησθον",
            "p1": "ἑπώμεθᾰ",
            "p2": "ἕπησθε",
            "p3": "ἕπωνται"
          },
          "optative": {
            "s1": "ἑποίμην",
            "s2": "ἕποιο",
            "s3": "ἕποιτο",
            "d2": "ἕποισθον",
            "d3": "ἑποίσθην",
            "p1": "ἑποίμεθᾰ",
            "p2": "ἕποισθε",
            "p3": "ἕποιντ"
          },
        },
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%B9%CF%83%CF%84%CE%B5%CF%8D%CF%89
    "type": "verb",
    "ground": "πῐστεύειν",
    "passport": "πῐστεύω",
    "dutch": "vertrouwen (op), geloven",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "πῐστεύω",
            "s2": "πῐστεύεις",
            "s3": "πῐστεύει",
            "d2": "πῐστεύετον",
            "d3": "πῐστεύετον",
            "p1": "πῐστεύομεν",
            "p2": "πῐστεύετε",
            "p3": "πῐστεύουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "πῐστεύω",
            "s2": "πῐστεύῃς",
            "s3": "πῐστεύῃ",
            "d2": "πῐστεύητον",
            "d3": "πῐστεύητον",
            "p1": "πῐστεύωμεν",
            "p2": "πῐστεύητε",
            "p3": "πῐστεύωσῐ(ν)"
          },
          "optative": {
            "s1": "πῐστεύοιμῐ",
            "s2": "πῐστεύοις",
            "s3": "πῐστεύοι",
            "d2": "πῐστεύοιτον",
            "d3": "πῐστευοίτην",
            "p1": "πῐστεύοιμεν",
            "p2": "πῐστεύοιτε",
            "p3": "πῐστεύοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "πῐστευε",
            "s3": "πῐστευέτω",
            "d2": "πῐστεύετον",
            "d3": "πῐστευέτων",
            "p1": "",
            "p2": "πῐστεύετε",
            "p3": "πῐστευόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "πῐστεύομαι",
            "s2": "πῐστεύῃ,πῐστεύει",
            "s3": "πῐστεύεται",
            "d2": "πῐστεύεσθον",
            "d3": "πῐστεύεσθον",
            "p1": "πῐστευόμεθᾰ",
            "p2": "πῐστεύεσθε",
            "p3": "πῐστεύονται"
          },
          "subjunctive": {
            "s1": "πῐστεύωμαι",
            "s2": "πῐστεύῃ",
            "s3": "πῐστεύηται",
            "d2": "πῐστεύησθον",
            "d3": "πῐστεύησθον",
            "p1": "πῐστευώμεθᾰ",
            "p2": "πῐστεύησθε",
            "p3": "πῐστεύωνται"
          },
          "optative": {
            "s1": "πῐστευοίμην",
            "s2": "πῐστεύοιο",
            "s3": "πῐστεύοιτο",
            "d2": "πῐστεύοισθον",
            "d3": "πῐστευοίσθην",
            "p1": "πῐστευοίμεθᾰ",
            "p2": "πῐστεύοισθε",
            "p3": "πῐστεύοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "πῐστεύου",
            "s3": "πῐστευέσθω",
            "d2": "πῐστεύεσθον",
            "d3": "πῐστευέσθων",
            "p1": "",
            "p2": "πῐστεύεσθε",
            "p3": "πῐστευέσθων"
          }
        },
        "infinitive": {
          "active": "πῐστεύειν",
          "middle": "πῐστεύεσθαι"
        },
        "participle": {
          "m": {
            "active": "πῐστεύων",
            "middle": "πῐστευόμενος"
          },
          "f": {
            "active": "πῐστεύουσᾰ",
            "middle": "πῐστευομένη"
          },
          "n": {
            "active": "πῐστεῦον",
            "middle": "πῐστευόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἐπῐστευον",
            "s2": "ἐπῐστευες",
            "s3": "ἐπῐστευε(ν)",
            "d2": "ἐπῐστεύετον",
            "d3": "ἐπῐστευέτην",
            "p1": "ἐπῐστεύομεν",
            "p2": "ἐπῐστεύετε",
            "p3": "ἐπῐστευον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἐπῐστευόμην",
            "s2": "ἐπῐστεύου",
            "s3": "ἐπῐστεύετο",
            "d2": "ἐπῐστεύεσθον",
            "d3": "ἐπῐστευέσθην",
            "p1": "ἐπῐστευόμεθᾰ",
            "p2": "ἐπῐστεύεσθε",
            "p3": "ἐπῐστεύοντο"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "πῐστεύσω",
            "s2": "πῐστεύσεις",
            "s3": "πῐστεύσει",
            "d2": "πῐστεύσετον",
            "d3": "πῐστεύσετον",
            "p1": "πῐστεύσομεν",
            "p2": "πῐστεύσετε",
            "p3": "πῐστεύσουσῐ(ν)"
          },
          "optative": {
            "s1": "πῐστεύσοιμῐ",
            "s2": "πῐστεύσοις",
            "s3": "πῐστεύσοι",
            "d2": "πῐστεύσοιτον",
            "d3": "πῐστευσοίτην",
            "p1": "πῐστεύσοιμεν",
            "p2": "πῐστεύσοιτε",
            "p3": "πῐστεύσοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "πῐστεύσομαι",
            "s2": "πῐστεύσῃ,πῐστεύσει",
            "s3": "πῐστεύσεται",
            "d2": "πῐστεύσεσθον",
            "d3": "πῐστεύσεσθον",
            "p1": "πῐστευσόμεθᾰ",
            "p2": "πῐστεύσεσθε",
            "p3": "πῐστεύσονται"
          },
          "optative": {
            "s1": "πῐστευσοίμην",
            "s2": "πῐστεύσοιο",
            "s3": "πῐστεύσοιτο",
            "d2": "πῐστεύσοισθον",
            "d3": "πῐστευσοίσθην",
            "p1": "πῐστευσοίμεθᾰ",
            "p2": "πῐστεύσοισθε",
            "p3": "πῐστεύσοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "πῐστευθήσομαι",
            "s2": "πῐστευθήσῃ",
            "s3": "πῐστευθήσεται",
            "d2": "πῐστευθήσεσθον",
            "d3": "πῐστευθήσεσθον",
            "p1": "πῐστευθησόμεθᾰ",
            "p2": "πῐστευθήσεσθε",
            "p3": "πῐστευθήσονται"
          },
          "optative": {
            "s1": "πῐστευθησοίμην",
            "s2": "πῐστευθήσοιο",
            "s3": "πῐστευθήσοιτο",
            "d2": "πῐστευθήσοισθον",
            "d3": "πῐστευθησοίσθην",
            "p1": "πῐστευθησοίμεθᾰ",
            "p2": "πῐστευθήσοισθε",
            "p3": "πῐστευθήσοιντο"
          }
        },
        "infinitive": {
          "active": "πῐστεύσειν",
          "middle": "πῐστεύσεσθαι",
          "passive": "πῐστευθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "πῐστεύσων",
            "middle": "πῐστευσόμενος",
            "passive": "πῐστευθησόμενος"
          },
          "f": {
            "active": "πῐστεύσουσᾰ",
            "middle": "πῐστευσομένη",
            "passive": "πῐστευθησομένη"
          },
          "n": {
            "active": "πῐστεῦσον",
            "middle": "πῐστευσόμενον",
            "passive": "πῐστευθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CF%84%CF%81%CE%AD%CF%80%CF%89
    "type": "verb",
    "ground": "τρέπειν",
    "passport": "τρέπω",
    "dutch": "lopen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "τρέπω",
            "s2": "τρέπεις",
            "s3": "τρέπει",
            "d2": "τρέπετον",
            "d3": "τρέπετον",
            "p1": "τρέπομεν",
            "p2": "τρέπετε",
            "p3": "τρέπουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "τρέπω",
            "s2": "τρέπῃς",
            "s3": "τρέπῃ",
            "d2": "τρέπητον",
            "d3": "τρέπητον",
            "p1": "τρέπωμεν",
            "p2": "τρέπητε",
            "p3": "τρέπωσῐ(ν)"
          },
          "optative": {
            "s1": "τρέποιμῐ",
            "s2": "τρέποις",
            "s3": "τρέποι",
            "d2": "τρέποιτον",
            "d3": "τρεποίτην",
            "p1": "τρέποιμεν",
            "p2": "τρέποιτε",
            "p3": "τρέποιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "τρέπε",
            "s3": "τρεπέτω",
            "d2": "τρέπετον",
            "d3": "τρεπέτων",
            "p1": "",
            "p2": "τρέπετε",
            "p3": "τρεπόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "τρέπομαι",
            "s2": "τρέπῃ,τρέπει",
            "s3": "τρέπεται",
            "d2": "τρέπεσθον",
            "d3": "τρέπεσθον",
            "p1": "τρεπόμεθᾰ",
            "p2": "τρέπεσθε",
            "p3": "τρέπονται"
          },
          "subjunctive": {
            "s1": "τρέπωμαι",
            "s2": "τρέπῃ",
            "s3": "τρέπηται",
            "d2": "τρέπησθον",
            "d3": "τρέπησθον",
            "p1": "τρεπώμεθᾰ",
            "p2": "τρέπησθε",
            "p3": "τρέπωνται"
          },
          "optative": {
            "s1": "τρεποίμην",
            "s2": "τρέποιο",
            "s3": "τρέποιτο",
            "d2": "τρέποισθον",
            "d3": "τρεποίσθην",
            "p1": "τρεποίμεθᾰ",
            "p2": "τρέποισθε",
            "p3": "τρέποιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "τρέπου",
            "s3": "τρεπέσθω",
            "d2": "τρέπεσθον",
            "d3": "τρεπέσθων",
            "p1": "",
            "p2": "τρέπεσθε",
            "p3": "τρεπέσθων"
          }
        },
        "infinitive": {
          "active": "τρέπειν",
          "middle": "τρέπεσθαι"
        },
        "participle": {
          "m": {
            "active": "τρέπων",
            "middle": "τρεπόμενος"
          },
          "f": {
            "active": "τρέπουσᾰ",
            "middle": "τρεπομένη"
          },
          "n": {
            "active": "τρέπον",
            "middle": "τρεπόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἔτρεπον",
            "s2": "ἔτρεπες",
            "s3": "ἔτρεπε(ν)",
            "d2": "ἐτρέπετον",
            "d3": "ἐτρεπέτην",
            "p1": "ἐτρέπομεν",
            "p2": "ἐτρέπετε",
            "p3": "ἔτρεπον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἐτρεπόμην",
            "s2": "ἐτρέπου",
            "s3": "ἐτρέπετο",
            "d2": "ἐτρέπεσθον",
            "d3": "ἐτρεπέσθην",
            "p1": "ἐτρεπόμεθᾰ",
            "p2": "ἐτρέπεσθε",
            "p3": "ἐτρέποντο"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "τρέψω",
            "s2": "τρέψεις",
            "s3": "τρέψει",
            "d2": "τρέψετον",
            "d3": "τρέψετον",
            "p1": "τρέψομεν",
            "p2": "τρέψετε",
            "p3": "τρέψουσῐ(ν)"
          },
          "optative": {
            "s1": "τρέψοιμῐ",
            "s2": "τρέψοις",
            "s3": "τρέψοι",
            "d2": "τρέψοιτον",
            "d3": "τρεψοίτην",
            "p1": "τρέψοιμεν",
            "p2": "τρέψοιτε",
            "p3": "τρέψοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "τρέψομαι",
            "s2": "τρέψῃ,τρέψει",
            "s3": "τρέψεται",
            "d2": "τρέψεσθον",
            "d3": "τρέψεσθον",
            "p1": "τρεψόμεθᾰ",
            "p2": "τρέψεσθε",
            "p3": "τρέψονται"
          },
          "optative": {
            "s1": "τρεψοίμην",
            "s2": "τρέψοιο",
            "s3": "τρέψοιτο",
            "d2": "τρέψοισθον",
            "d3": "τρεψοίσθην",
            "p1": "τρεψοίμεθᾰ",
            "p2": "τρέψοισθε",
            "p3": "τρέψοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "τραπήσομαι",
            "s2": "τραπήσῃ",
            "s3": "τραπήσεται",
            "d2": "τραπήσεσθον",
            "d3": "τραπήσεσθον",
            "p1": "τραπησόμεθᾰ",
            "p2": "τραπήσεσθε",
            "p3": "τραπήσονται"
          },
          "optative": {
            "s1": "τραπησοίμην",
            "s2": "τραπήσοιο",
            "s3": "τραπήσοιτο",
            "d2": "τραπήσοισθον",
            "d3": "τραπησοίσθην",
            "p1": "τραπησοίμεθᾰ",
            "p2": "τραπήσοισθε",
            "p3": "τραπήσοιντο"
          }
        },
        "infinitive": {
          "active": "τρέψειν",
          "middle": "τρέψεσθαι",
          "passive": "τραπήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "τρέψων",
            "middle": "τρεψόμενος",
            "passive": "τραπησόμενος"
          },
          "f": {
            "active": "τρέψουσᾰ",
            "middle": "τρεψομένη",
            "passive": "τραπησομένη"
          },
          "n": {
            "active": "τρέψον",
            "middle": "τρεψόμενον",
            "passive": "τραπησόμενον"
          }
        }
      }
    }
  },
  // https://en.wiktionary.org/wiki/%E1%BC%80%CF%80%CE%BF%CE%B8%CE%BD%E1%BF%84%CF%83%CE%BA%CF%89
  {
    "type": "verb",
    "ground": "ἀποθνῄσκειν",
    "passport": "ἀποθνῄσκω",
    "dutch": "sterven",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "ἀποθνῄσκω",
            "s2": "ἀποθνῄσκεις",
            "s3": "ἀποθνῄσκει",
            "d2": "ἀποθνῄσκετον",
            "d3": "ἀποθνῄσκετον",
            "p1": "ἀποθνῄσκομεν",
            "p2": "ἀποθνῄσκετε",
            "p3": "ἀποθνῄσκουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "ἀποθνῄσκω",
            "s2": "ἀποθνῄσκῃς",
            "s3": "ἀποθνῄσκῃ",
            "d2": "ἀποθνῄσκητον",
            "d3": "ἀποθνῄσκητον",
            "p1": "ἀποθνῄσκωμεν",
            "p2": "ἀποθνῄσκητε",
            "p3": "ἀποθνῄσκωσῐ(ν)"
          },
          "optative": {
            "s1": "ἀποθνῄσκοιμῐ",
            "s2": "ἀποθνῄσκοις",
            "s3": "ἀποθνῄσκοι",
            "d2": "ἀποθνῄσκοιτον",
            "d3": "ἀποθνῃσκοίτην",
            "p1": "ἀποθνῄσκοιμεν",
            "p2": "ἀποθνῄσκοιτε",
            "p3": "ἀποθνῄσκοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "ἀπόθνῃσκε",
            "s3": "ἀποθνῃσκέτω",
            "d2": "ἀποθνῄσκετον",
            "d3": "ἀποθνῃσκέτων",
            "p1": "",
            "p2": "ἀποθνῄσκετε",
            "p3": "ἀποθνῃσκόντων"
          }
        },
        "infinitive": {
          "active": "ἀποθνῄσκειν",
          "middle": ""
        },
        "participle": {
          "m": {
            "active": "ἀποθνῄσκων",
            "middle": ""
          },
          "f": {
            "active": "ἀποθνῄσκουσᾰ",
            "middle": ""
          },
          "n": {
            "active": "ἀποθνῇσκον",
            "middle": ""
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἀπέθνῃσκον",
            "s2": "ἀπέθνῃσκες",
            "s3": "ἀπέθνῃσκε(ν)",
            "d2": "ἀπεθνῄσκετον",
            "d3": "ἀπεθνῃσκέτην",
            "p1": "ἀπεθνῄσκομεν",
            "p2": "ἀπεθνῄσκετε",
            "p3": "ἀπέθνῃσκον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CF%85%CE%BD%CE%B8%CE%AC%CE%BD%CE%BF%CE%BC%CE%B1%CE%B9
    type: "verb",
    dutch: "vernemen, navraag doen",
    ground: "πῠνθᾰ́νεσθαι",
    passport: "πῠνθᾰ́νομαι",
    explicitConjugations: {
      present: {
        middlePassive: {
          indicative: {
            "s1": "πῠνθᾰ́νομαι",
            "s2": "πῠνθᾰ́νῃ,πῠνθᾰ́νει",
            "s3": "πῠνθᾰ́νεται",
            "d2": "πῠνθᾰ́νεσθον",
            "d3": "πῠνθᾰ́νεσθον",
            "p1": "πῠνθᾰνόμεθᾰ",
            "p2": "πῠνθᾰ́νεσθε",
            "p3": "πῠνθᾰ́νοντα",
          }
        }
      },
    },
  },
  {
    // https://en.wiktionary.org/wiki/%CF%88%CE%B5%CF%8D%CE%B4%CF%89
    "type": "verb",
    "ground": "ψεύδειν",
    "passport": "ψεύδω",
    "dutch": "bedriegen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "ψεύδω",
            "s2": "ψεύδεις",
            "s3": "ψεύδει",
            "d2": "ψεύδετον",
            "d3": "ψεύδετον",
            "p1": "ψεύδομεν",
            "p2": "ψεύδετε",
            "p3": "ψεύδουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "ψεύδω",
            "s2": "ψεύδῃς",
            "s3": "ψεύδῃ",
            "d2": "ψεύδητον",
            "d3": "ψεύδητον",
            "p1": "ψεύδωμεν",
            "p2": "ψεύδητε",
            "p3": "ψεύδωσῐ(ν)"
          },
          "optative": {
            "s1": "ψεύδοιμῐ",
            "s2": "ψεύδοις",
            "s3": "ψεύδοι",
            "d2": "ψεύδοιτον",
            "d3": "ψευδοίτην",
            "p1": "ψεύδοιμεν",
            "p2": "ψεύδοιτε",
            "p3": "ψεύδοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "ψεῦδε",
            "s3": "ψευδέτω",
            "d2": "ψεύδετον",
            "d3": "ψευδέτων",
            "p1": "",
            "p2": "ψεύδετε",
            "p3": "ψευδόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ψεύδομαι",
            "s2": "ψεύδῃ,ψεύδει",
            "s3": "ψεύδεται",
            "d2": "ψεύδεσθον",
            "d3": "ψεύδεσθον",
            "p1": "ψευδόμεθᾰ",
            "p2": "ψεύδεσθε",
            "p3": "ψεύδονται"
          },
          "subjunctive": {
            "s1": "ψεύδωμαι",
            "s2": "ψεύδῃ",
            "s3": "ψεύδηται",
            "d2": "ψεύδησθον",
            "d3": "ψεύδησθον",
            "p1": "ψευδώμεθᾰ",
            "p2": "ψεύδησθε",
            "p3": "ψεύδωνται"
          },
          "optative": {
            "s1": "ψευδοίμην",
            "s2": "ψεύδοιο",
            "s3": "ψεύδοιτο",
            "d2": "ψεύδοισθον",
            "d3": "ψευδοίσθην",
            "p1": "ψευδοίμεθᾰ",
            "p2": "ψεύδοισθε",
            "p3": "ψεύδοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "ψεύδου",
            "s3": "ψευδέσθω",
            "d2": "ψεύδεσθον",
            "d3": "ψευδέσθων",
            "p1": "",
            "p2": "ψεύδεσθε",
            "p3": "ψευδέσθων"
          }
        },
        "infinitive": {
          "active": "ψεύδειν",
          "middle": "ψεύδεσθαι"
        },
        "participle": {
          "m": {
            "active": "ψεύδων",
            "middle": "ψευδόμενος"
          },
          "f": {
            "active": "ψεύδουσᾰ",
            "middle": "ψευδομένη"
          },
          "n": {
            "active": "ψεῦδον",
            "middle": "ψευδόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἔψευδον",
            "s2": "ἔψευδες",
            "s3": "ἔψευδε(ν)",
            "d2": "ἐψεύδετον",
            "d3": "ἐψευδέτην",
            "p1": "ἐψεύδομεν",
            "p2": "ἐψεύδετε",
            "p3": "ἔψευδον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἐψευδόμην",
            "s2": "ἐψεύδου",
            "s3": "ἐψεύδετο",
            "d2": "ἐψεύδεσθον",
            "d3": "ἐψευδέσθην",
            "p1": "ἐψευδόμεθᾰ",
            "p2": "ἐψεύδεσθε",
            "p3": "ἐψεύδοντο"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "ψεύσω",
            "s2": "ψεύσεις",
            "s3": "ψεύσει",
            "d2": "ψεύσετον",
            "d3": "ψεύσετον",
            "p1": "ψεύσομεν",
            "p2": "ψεύσετε",
            "p3": "ψεύσουσῐ(ν)"
          },
          "optative": {
            "s1": "ψεύσοιμῐ",
            "s2": "ψεύσοις",
            "s3": "ψεύσοι",
            "d2": "ψεύσοιτον",
            "d3": "ψευσοίτην",
            "p1": "ψεύσοιμεν",
            "p2": "ψεύσοιτε",
            "p3": "ψεύσοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "ψεύσομαι",
            "s2": "ψεύσῃ,ψεύσει",
            "s3": "ψεύσεται",
            "d2": "ψεύσεσθον",
            "d3": "ψεύσεσθον",
            "p1": "ψευσόμεθᾰ",
            "p2": "ψεύσεσθε",
            "p3": "ψεύσονται"
          },
          "optative": {
            "s1": "ψευσοίμην",
            "s2": "ψεύσοιο",
            "s3": "ψεύσοιτο",
            "d2": "ψεύσοισθον",
            "d3": "ψευσοίσθην",
            "p1": "ψευσοίμεθᾰ",
            "p2": "ψεύσοισθε",
            "p3": "ψεύσοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "ψευσθήσομαι",
            "s2": "ψευσθήσῃ",
            "s3": "ψευσθήσεται",
            "d2": "ψευσθήσεσθον",
            "d3": "ψευσθήσεσθον",
            "p1": "ψευσθησόμεθᾰ",
            "p2": "ψευσθήσεσθε",
            "p3": "ψευσθήσονται"
          },
          "optative": {
            "s1": "ψευσθησοίμην",
            "s2": "ψευσθήσοιο",
            "s3": "ψευσθήσοιτο",
            "d2": "ψευσθήσοισθον",
            "d3": "ψευσθησοίσθην",
            "p1": "ψευσθησοίμεθᾰ",
            "p2": "ψευσθήσοισθε",
            "p3": "ψευσθήσοιντο"
          }
        },
        "infinitive": {
          "active": "ψεύσειν",
          "middle": "ψεύσεσθαι",
          "passive": "ψευσθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "ψεύσων",
            "middle": "ψευσόμενος",
            "passive": "ψευσθησόμενος"
          },
          "f": {
            "active": "ψεύσουσᾰ",
            "middle": "ψευσομένη",
            "passive": "ψευσθησομένη"
          },
          "n": {
            "active": "ψεῦσον",
            "middle": "ψευσόμενον",
            "passive": "ψευσθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BA%CF%81%CF%8D%CF%80%CF%84%CF%89
    "type": "verb",
    "ground": "κρῠπτειν",
    "passport": "κρῠπτω",
    "dutch": "verbergen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "κρῠπτω",
            "s2": "κρῠπτεις",
            "s3": "κρῠπτει",
            "d2": "κρῠπτετον",
            "d3": "κρῠπτετον",
            "p1": "κρῠπτομεν",
            "p2": "κρῠπτετε",
            "p3": "κρῠπτουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "κρῠπτω",
            "s2": "κρῠπτῃς",
            "s3": "κρῠπτῃ",
            "d2": "κρῠπτητον",
            "d3": "κρῠπτητον",
            "p1": "κρῠπτωμεν",
            "p2": "κρῠπτητε",
            "p3": "κρῠπτωσῐ(ν)"
          },
          "optative": {
            "s1": "κρῠπτοιμῐ",
            "s2": "κρῠπτοις",
            "s3": "κρῠπτοι",
            "d2": "κρῠπτοιτον",
            "d3": "κρῠπτοίτην",
            "p1": "κρῠπτοιμεν",
            "p2": "κρῠπτοιτε",
            "p3": "κρῠπτοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "κρῠπτε",
            "s3": "κρῠπτέτω",
            "d2": "κρῠπτετον",
            "d3": "κρῠπτέτων",
            "p1": "",
            "p2": "κρῠπτετε",
            "p3": "κρῠπτόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "κρῠπτομαι",
            "s2": "κρῠπτῃ,κρῠπτει",
            "s3": "κρῠπτεται",
            "d2": "κρῠπτεσθον",
            "d3": "κρῠπτεσθον",
            "p1": "κρῠπτόμεθᾰ",
            "p2": "κρῠπτεσθε",
            "p3": "κρῠπτονται"
          },
          "subjunctive": {
            "s1": "κρῠπτωμαι",
            "s2": "κρῠπτῃ",
            "s3": "κρῠπτηται",
            "d2": "κρῠπτησθον",
            "d3": "κρῠπτησθον",
            "p1": "κρῠπτώμεθᾰ",
            "p2": "κρῠπτησθε",
            "p3": "κρῠπτωνται"
          },
          "optative": {
            "s1": "κρῠπτοίμην",
            "s2": "κρῠπτοιο",
            "s3": "κρῠπτοιτο",
            "d2": "κρῠπτοισθον",
            "d3": "κρῠπτοίσθην",
            "p1": "κρῠπτοίμεθᾰ",
            "p2": "κρῠπτοισθε",
            "p3": "κρῠπτοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "κρῠπτου",
            "s3": "κρῠπτέσθω",
            "d2": "κρῠπτεσθον",
            "d3": "κρῠπτέσθων",
            "p1": "",
            "p2": "κρῠπτεσθε",
            "p3": "κρῠπτέσθων"
          }
        },
        "infinitive": {
          "active": "κρῠπτειν",
          "middle": "κρῠπτεσθαι"
        },
        "participle": {
          "m": {
            "active": "κρῠπτων",
            "middle": "κρῠπτόμενος"
          },
          "f": {
            "active": "κρῠπτουσᾰ",
            "middle": "κρῠπτομένη"
          },
          "n": {
            "active": "κρῠπτον",
            "middle": "κρῠπτόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἔκρῠπτον",
            "s2": "ἔκρῠπτες",
            "s3": "ἔκρῠπτε(ν)",
            "d2": "ἐκρῠπτετον",
            "d3": "ἐκρῠπτέτην",
            "p1": "ἐκρῠπτομεν",
            "p2": "ἐκρῠπτετε",
            "p3": "ἔκρῠπτον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἐκρῠπτόμην",
            "s2": "ἐκρῠπτου",
            "s3": "ἐκρῠπτετο",
            "d2": "ἐκρῠπτεσθον",
            "d3": "ἐκρῠπτέσθην",
            "p1": "ἐκρῠπτόμεθᾰ",
            "p2": "ἐκρῠπτεσθε",
            "p3": "ἐκρῠπτοντο"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "κρῠψω",
            "s2": "κρῠψεις",
            "s3": "κρῠψει",
            "d2": "κρῠψετον",
            "d3": "κρῠψετον",
            "p1": "κρῠψομεν",
            "p2": "κρῠψετε",
            "p3": "κρῠψουσῐ(ν)"
          },
          "optative": {
            "s1": "κρῠψοιμῐ",
            "s2": "κρῠψοις",
            "s3": "κρῠψοι",
            "d2": "κρῠψοιτον",
            "d3": "κρῠψοίτην",
            "p1": "κρῠψοιμεν",
            "p2": "κρῠψοιτε",
            "p3": "κρῠψοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "κρῠψομαι",
            "s2": "κρῠψῃ,κρῠψει",
            "s3": "κρῠψεται",
            "d2": "κρῠψεσθον",
            "d3": "κρῠψεσθον",
            "p1": "κρῠψόμεθᾰ",
            "p2": "κρῠψεσθε",
            "p3": "κρῠψονται"
          },
          "optative": {
            "s1": "κρῠψοίμην",
            "s2": "κρῠψοιο",
            "s3": "κρῠψοιτο",
            "d2": "κρῠψοισθον",
            "d3": "κρῠψοίσθην",
            "p1": "κρῠψοίμεθᾰ",
            "p2": "κρῠψοισθε",
            "p3": "κρῠψοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "κρῠφθήσομαι",
            "s2": "κρῠφθήσῃ",
            "s3": "κρῠφθήσεται",
            "d2": "κρῠφθήσεσθον",
            "d3": "κρῠφθήσεσθον",
            "p1": "κρῠφθησόμεθᾰ",
            "p2": "κρῠφθήσεσθε",
            "p3": "κρῠφθήσονται"
          },
          "optative": {
            "s1": "κρῠφθησοίμην",
            "s2": "κρῠφθήσοιο",
            "s3": "κρῠφθήσοιτο",
            "d2": "κρῠφθήσοισθον",
            "d3": "κρῠφθησοίσθην",
            "p1": "κρῠφθησοίμεθᾰ",
            "p2": "κρῠφθήσοισθε",
            "p3": "κρῠφθήσοιντο"
          }
        },
        "infinitive": {
          "active": "κρῠψειν",
          "middle": "κρῠψεσθαι",
          "passive": "κρῠφθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "κρῠψων",
            "middle": "κρῠψόμενος",
            "passive": "κρῠφθησόμενος"
          },
          "f": {
            "active": "κρῠψουσᾰ",
            "middle": "κρῠψομένη",
            "passive": "κρῠφθησομένη"
          },
          "n": {
            "active": "κρῠψον",
            "middle": "κρῠψόμενον",
            "passive": "κρῠφθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CE%B9%CE%B1%CE%BB%CE%AD%CE%B3%CE%BF%CE%BC%CE%B1%CE%B9
    "type": "verb",
    "ground": "δῐᾰλέγεσθαι",
    "passport": "δῐᾰλέγομαι (+ dat.)",
    "dutch": "een gesprek voeren (met)",
    "explicitConjugations": {
      "present": {
        "middlePassive": {
          "indicative": {
            "s1": "δῐᾰλέγομαι",
            "s2": "δῐᾰλέγῃ,δῐᾰλέγει",
            "s3": "δῐᾰλέγεται",
            "d2": "δῐᾰλέγεσθον",
            "d3": "δῐᾰλέγεσθον",
            "p1": "δῐᾰλεγόμεθᾰ",
            "p2": "δῐᾰλέγεσθε",
            "p3": "δῐᾰλέγονται"
          },
          "subjunctive": {
            "s1": "δῐᾰλέγωμαι",
            "s2": "δῐᾰλέγῃ",
            "s3": "δῐᾰλέγηται",
            "d2": "δῐᾰλέγησθον",
            "d3": "δῐᾰλέγησθον",
            "p1": "δῐᾰλεγώμεθᾰ",
            "p2": "δῐᾰλέγησθε",
            "p3": "δῐᾰλέγωνται"
          },
          "optative": {
            "s1": "δῐᾰλεγοίμην",
            "s2": "δῐᾰλέγοιο",
            "s3": "δῐᾰλέγοιτο",
            "d2": "δῐᾰλέγοισθον",
            "d3": "δῐᾰλεγοίσθην",
            "p1": "δῐᾰλεγοίμεθᾰ",
            "p2": "δῐᾰλέγοισθε",
            "p3": "δῐᾰλέγοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "δῐᾰλέγου",
            "s3": "δῐᾰλεγέσθω",
            "d2": "δῐᾰλέγεσθον",
            "d3": "δῐᾰλεγέσθων",
            "p1": "",
            "p2": "δῐᾰλέγεσθε",
            "p3": "δῐᾰλεγέσθων"
          }
        },
        "infinitive": {
          "active": "",
          "middle": "δῐᾰλέγεσθαι"
        },
        "participle": {
          "m": {
            "active": "",
            "middle": "δῐᾰλεγόμενος"
          },
          "f": {
            "active": "",
            "middle": "δῐᾰλεγομένη"
          },
          "n": {
            "active": "",
            "middle": "δῐᾰλεγόμενον"
          }
        }
      },
      "imperfect": {
        "middlePassive": {
          "indicative": {
            "s1": "δῐελεγόμην",
            "s2": "δῐελέγου",
            "s3": "δῐελέγετο",
            "d2": "δῐελέγεσθον",
            "d3": "δῐελεγέσθην",
            "p1": "δῐελεγόμεθᾰ",
            "p2": "δῐελέγεσθε",
            "p3": "δῐελέγοντο"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B8%CF%8D%CF%89
    "type": "verb",
    "ground": "θῡ́ειν",
    "passport": "θῡ́ω",
    "dutch": "offeren",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "θῡ́ω",
            "s2": "θῡ́εις",
            "s3": "θῡ́ει",
            "d2": "θῡ́ετον",
            "d3": "θῡ́ετον",
            "p1": "θῡ́ομεν",
            "p2": "θῡ́ετε",
            "p3": "θῡ́ουσῐ(ν)",
          },
        },
        "infinitive": {
          "active": "θῡ́ειν",
          "middle": "θῡ́εσθαι",
        },
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "θῡσω",
            "s2": "θῡσεις",
            "s3": "θῡσει",
            "d2": "θῡσετον",
            "d3": "θῡσετον",
            "p1": "θῡσομεν",
            "p2": "θῡσετε",
            "p3": "θῡσουσῐ(ν)"
          },
          "optative": {
            "s1": "θῡσοιμῐ",
            "s2": "θῡσοις",
            "s3": "θῡσοι",
            "d2": "θῡσοιτον",
            "d3": "θῡσοίτην",
            "p1": "θῡσοιμεν",
            "p2": "θῡσοιτε",
            "p3": "θῡσοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "θῡσομαι",
            "s2": "θῡσῃ,θῡσει",
            "s3": "θῡσεται",
            "d2": "θῡσεσθον",
            "d3": "θῡσεσθον",
            "p1": "θῡσόμεθᾰ",
            "p2": "θῡσεσθε",
            "p3": "θῡσονται"
          },
          "optative": {
            "s1": "θῡσοίμην",
            "s2": "θῡσοιο",
            "s3": "θῡσοιτο",
            "d2": "θῡσοισθον",
            "d3": "θῡσοίσθην",
            "p1": "θῡσοίμεθᾰ",
            "p2": "θῡσοισθε",
            "p3": "θῡσοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "τῠθήσομαι",
            "s2": "τῠθήσῃ",
            "s3": "τῠθήσεται",
            "d2": "τῠθήσεσθον",
            "d3": "τῠθήσεσθον",
            "p1": "τῠθησόμεθᾰ",
            "p2": "τῠθήσεσθε",
            "p3": "τῠθήσονται"
          },
          "optative": {
            "s1": "τῠθησοίμην",
            "s2": "τῠθήσοιο",
            "s3": "τῠθήσοιτο",
            "d2": "τῠθήσοισθον",
            "d3": "τῠθησοίσθην",
            "p1": "τῠθησοίμεθᾰ",
            "p2": "τῠθήσοισθε",
            "p3": "τῠθήσοιντο"
          }
        },
        "infinitive": {
          "active": "θῡσειν",
          "middle": "θῡσεσθαι",
          "passive": "τῠθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "θῡσων",
            "middle": "θῡσόμενος",
            "passive": "τῠθησόμενος"
          },
          "f": {
            "active": "θῡσουσᾰ",
            "middle": "θῡσομένη",
            "passive": "τῠθησομένη"
          },
          "n": {
            "active": "θῦσον",
            "middle": "θῡσόμενον",
            "passive": "τῠθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%AF%CF%80%CF%84%CF%89
    "type": "verb",
    "ground": "πῑπτειν",
    "passport": "πῑπτω",
    "dutch": "vallen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "πῑπτω",
            "s2": "πῑπτεις",
            "s3": "πῑπτει",
            "d2": "πῑπτετον",
            "d3": "πῑπτετον",
            "p1": "πῑπτομεν",
            "p2": "πῑπτετε",
            "p3": "πῑπτουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "πῑπτω",
            "s2": "πῑπτῃς",
            "s3": "πῑπτῃ",
            "d2": "πῑπτητον",
            "d3": "πῑπτητον",
            "p1": "πῑπτωμεν",
            "p2": "πῑπτητε",
            "p3": "πῑπτωσῐ(ν)"
          },
          "optative": {
            "s1": "πῑπτοιμῐ",
            "s2": "πῑπτοις",
            "s3": "πῑπτοι",
            "d2": "πῑπτοιτον",
            "d3": "πῑπτοίτην",
            "p1": "πῑπτοιμεν",
            "p2": "πῑπτοιτε",
            "p3": "πῑπτοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "πῖπτε",
            "s3": "πῑπτέτω",
            "d2": "πῑπτετον",
            "d3": "πῑπτέτων",
            "p1": "",
            "p2": "πῑπτετε",
            "p3": "πῑπτόντων"
          }
        },
        "infinitive": {
          "active": "πῑπτειν",
          "middle": ""
        },
        "participle": {
          "m": {
            "active": "πῑπτων",
            "middle": ""
          },
          "f": {
            "active": "πῑπτουσᾰ",
            "middle": ""
          },
          "n": {
            "active": "πῖπτον",
            "middle": ""
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CE%BD%CE%B1%CE%B3%CE%BA%CE%AC%CE%B6%CF%89
    "type": "verb",
    "ground": "ᾰ̓νᾰγκᾰζειν",
    "passport": "ᾰ̓νᾰγκᾰζω",
    "dutch": "dwingen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓νᾰγκᾰζω",
            "s2": "ᾰ̓νᾰγκᾰζεις",
            "s3": "ᾰ̓νᾰγκᾰζει",
            "d2": "ᾰ̓νᾰγκᾰζετον",
            "d3": "ᾰ̓νᾰγκᾰζετον",
            "p1": "ᾰ̓νᾰγκᾰζομεν",
            "p2": "ᾰ̓νᾰγκᾰζετε",
            "p3": "ᾰ̓νᾰγκᾰζουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "ᾰ̓νᾰγκᾰζω",
            "s2": "ᾰ̓νᾰγκᾰζῃς",
            "s3": "ᾰ̓νᾰγκᾰζῃ",
            "d2": "ᾰ̓νᾰγκᾰζητον",
            "d3": "ᾰ̓νᾰγκᾰζητον",
            "p1": "ᾰ̓νᾰγκᾰζωμεν",
            "p2": "ᾰ̓νᾰγκᾰζητε",
            "p3": "ᾰ̓νᾰγκᾰζωσῐ(ν)"
          },
          "optative": {
            "s1": "ᾰ̓νᾰγκᾰζοιμῐ",
            "s2": "ᾰ̓νᾰγκᾰζοις",
            "s3": "ᾰ̓νᾰγκᾰζοι",
            "d2": "ᾰ̓νᾰγκᾰζοιτον",
            "d3": "ᾰ̓νᾰγκᾰζοίτην",
            "p1": "ᾰ̓νᾰγκᾰζοιμεν",
            "p2": "ᾰ̓νᾰγκᾰζοιτε",
            "p3": "ᾰ̓νᾰγκᾰζοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "ᾰ̓νᾰγκᾰζε",
            "s3": "ᾰ̓νᾰγκᾰζέτω",
            "d2": "ᾰ̓νᾰγκᾰζετον",
            "d3": "ᾰ̓νᾰγκᾰζέτων",
            "p1": "",
            "p2": "ᾰ̓νᾰγκᾰζετε",
            "p3": "ᾰ̓νᾰγκᾰζόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ᾰ̓νᾰγκᾰζομαι",
            "s2": "ᾰ̓νᾰγκᾰζῃ,ᾰ̓νᾰγκᾰζει",
            "s3": "ᾰ̓νᾰγκᾰζεται",
            "d2": "ᾰ̓νᾰγκᾰζεσθον",
            "d3": "ᾰ̓νᾰγκᾰζεσθον",
            "p1": "ᾰ̓νᾰγκᾰζόμεθᾰ",
            "p2": "ᾰ̓νᾰγκᾰζεσθε",
            "p3": "ᾰ̓νᾰγκᾰζονται"
          },
          "subjunctive": {
            "s1": "ᾰ̓νᾰγκᾰζωμαι",
            "s2": "ᾰ̓νᾰγκᾰζῃ",
            "s3": "ᾰ̓νᾰγκᾰζηται",
            "d2": "ᾰ̓νᾰγκᾰζησθον",
            "d3": "ᾰ̓νᾰγκᾰζησθον",
            "p1": "ᾰ̓νᾰγκᾰζώμεθᾰ",
            "p2": "ᾰ̓νᾰγκᾰζησθε",
            "p3": "ᾰ̓νᾰγκᾰζωνται"
          },
          "optative": {
            "s1": "ᾰ̓νᾰγκᾰζοίμην",
            "s2": "ᾰ̓νᾰγκᾰζοιο",
            "s3": "ᾰ̓νᾰγκᾰζοιτο",
            "d2": "ᾰ̓νᾰγκᾰζοισθον",
            "d3": "ᾰ̓νᾰγκᾰζοίσθην",
            "p1": "ᾰ̓νᾰγκᾰζοίμεθᾰ",
            "p2": "ᾰ̓νᾰγκᾰζοισθε",
            "p3": "ᾰ̓νᾰγκᾰζοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "ᾰ̓νᾰγκᾰζου",
            "s3": "ᾰ̓νᾰγκᾰζέσθω",
            "d2": "ᾰ̓νᾰγκᾰζεσθον",
            "d3": "ᾰ̓νᾰγκᾰζέσθων",
            "p1": "",
            "p2": "ᾰ̓νᾰγκᾰζεσθε",
            "p3": "ᾰ̓νᾰγκᾰζέσθων"
          }
        },
        "infinitive": {
          "active": "ᾰ̓νᾰγκᾰζειν",
          "middle": "ᾰ̓νᾰγκᾰζεσθαι"
        },
        "participle": {
          "m": {
            "active": "ᾰ̓νᾰγκᾰζων",
            "middle": "ᾰ̓νᾰγκᾰζόμενος"
          },
          "f": {
            "active": "ᾰ̓νᾰγκᾰζουσᾰ",
            "middle": "ᾰ̓νᾰγκᾰζομένη"
          },
          "n": {
            "active": "ᾰ̓νᾰγκᾰζον",
            "middle": "ᾰ̓νᾰγκᾰζόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἠνᾰγκᾰζον",
            "s2": "ἠνᾰγκᾰζες",
            "s3": "ἠνᾰγκᾰζε(ν)",
            "d2": "ἠνᾰγκᾰζετον",
            "d3": "ἠνᾰγκᾰζέτην",
            "p1": "ἠνᾰγκᾰζομεν",
            "p2": "ἠνᾰγκᾰζετε",
            "p3": "ἠνᾰγκᾰζον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἠνᾰγκᾰζόμην",
            "s2": "ἠνᾰγκᾰζου",
            "s3": "ἠνᾰγκᾰζετο",
            "d2": "ἠνᾰγκᾰζεσθον",
            "d3": "ἠνᾰγκᾰζέσθην",
            "p1": "ἠνᾰγκᾰζόμεθᾰ",
            "p2": "ἠνᾰγκᾰζεσθε",
            "p3": "ἠνᾰγκᾰζοντο"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓νᾰγκᾰσω",
            "s2": "ᾰ̓νᾰγκᾰσεις",
            "s3": "ᾰ̓νᾰγκᾰσει",
            "d2": "ᾰ̓νᾰγκᾰσετον",
            "d3": "ᾰ̓νᾰγκᾰσετον",
            "p1": "ᾰ̓νᾰγκᾰσομεν",
            "p2": "ᾰ̓νᾰγκᾰσετε",
            "p3": "ᾰ̓νᾰγκᾰσουσῐ(ν)"
          },
          "optative": {
            "s1": "ᾰ̓νᾰγκᾰσοιμῐ",
            "s2": "ᾰ̓νᾰγκᾰσοις",
            "s3": "ᾰ̓νᾰγκᾰσοι",
            "d2": "ᾰ̓νᾰγκᾰσοιτον",
            "d3": "ᾰ̓νᾰγκᾰσοίτην",
            "p1": "ᾰ̓νᾰγκᾰσοιμεν",
            "p2": "ᾰ̓νᾰγκᾰσοιτε",
            "p3": "ᾰ̓νᾰγκᾰσοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "ᾰ̓νᾰγκᾰσομαι",
            "s2": "ᾰ̓νᾰγκᾰσῃ,ᾰ̓νᾰγκᾰσει",
            "s3": "ᾰ̓νᾰγκᾰσεται",
            "d2": "ᾰ̓νᾰγκᾰσεσθον",
            "d3": "ᾰ̓νᾰγκᾰσεσθον",
            "p1": "ᾰ̓νᾰγκᾰσόμεθᾰ",
            "p2": "ᾰ̓νᾰγκᾰσεσθε",
            "p3": "ᾰ̓νᾰγκᾰσονται"
          },
          "optative": {
            "s1": "ᾰ̓νᾰγκᾰσοίμην",
            "s2": "ᾰ̓νᾰγκᾰσοιο",
            "s3": "ᾰ̓νᾰγκᾰσοιτο",
            "d2": "ᾰ̓νᾰγκᾰσοισθον",
            "d3": "ᾰ̓νᾰγκᾰσοίσθην",
            "p1": "ᾰ̓νᾰγκᾰσοίμεθᾰ",
            "p2": "ᾰ̓νᾰγκᾰσοισθε",
            "p3": "ᾰ̓νᾰγκᾰσοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "ᾰ̓νᾰγκᾰσθήσομαι",
            "s2": "ᾰ̓νᾰγκᾰσθήσῃ",
            "s3": "ᾰ̓νᾰγκᾰσθήσεται",
            "d2": "ᾰ̓νᾰγκᾰσθήσεσθον",
            "d3": "ᾰ̓νᾰγκᾰσθήσεσθον",
            "p1": "ᾰ̓νᾰγκᾰσθησόμεθᾰ",
            "p2": "ᾰ̓νᾰγκᾰσθήσεσθε",
            "p3": "ᾰ̓νᾰγκᾰσθήσονται"
          },
          "optative": {
            "s1": "ᾰ̓νᾰγκᾰσθησοίμην",
            "s2": "ᾰ̓νᾰγκᾰσθήσοιο",
            "s3": "ᾰ̓νᾰγκᾰσθήσοιτο",
            "d2": "ᾰ̓νᾰγκᾰσθήσοισθον",
            "d3": "ᾰ̓νᾰγκᾰσθησοίσθην",
            "p1": "ᾰ̓νᾰγκᾰσθησοίμεθᾰ",
            "p2": "ᾰ̓νᾰγκᾰσθήσοισθε",
            "p3": "ᾰ̓νᾰγκᾰσθήσοιντο"
          }
        },
        "infinitive": {
          "active": "ᾰ̓νᾰγκᾰσειν",
          "middle": "ᾰ̓νᾰγκᾰσεσθαι",
          "passive": "ᾰ̓νᾰγκᾰσθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "ᾰ̓νᾰγκᾰσων",
            "middle": "ᾰ̓νᾰγκᾰσόμενος",
            "passive": "ᾰ̓νᾰγκᾰσθησόμενος"
          },
          "f": {
            "active": "ᾰ̓νᾰγκᾰσουσᾰ",
            "middle": "ᾰ̓νᾰγκᾰσομένη",
            "passive": "ᾰ̓νᾰγκᾰσθησομένη"
          },
          "n": {
            "active": "ᾰ̓νᾰγκᾰσον",
            "middle": "ᾰ̓νᾰγκᾰσόμενον",
            "passive": "ᾰ̓νᾰγκᾰσθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CE%B3%CE%B3%CE%AD%CE%BB%CE%BB%CF%89
    "type": "verb",
    "ground": "ἀγγέλλειν",
    "passport": "ἀγγέλλω",
    "dutch": "melden",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "ἀγγέλλω",
            "s2": "ἀγγέλλεις",
            "s3": "ἀγγέλλει",
            "d2": "ἀγγέλλετον",
            "d3": "ἀγγέλλετον",
            "p1": "ἀγγέλλομεν",
            "p2": "ἀγγέλλετε",
            "p3": "ἀγγέλλουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "ἀγγέλλω",
            "s2": "ἀγγέλλῃς",
            "s3": "ἀγγέλλῃ",
            "d2": "ἀγγέλλητον",
            "d3": "ἀγγέλλητον",
            "p1": "ἀγγέλλωμεν",
            "p2": "ἀγγέλλητε",
            "p3": "ἀγγέλλωσῐ(ν)"
          },
          "optative": {
            "s1": "ἀγγέλλοιμῐ",
            "s2": "ἀγγέλλοις",
            "s3": "ἀγγέλλοι",
            "d2": "ἀγγέλλοιτον",
            "d3": "ἀγγελλοίτην",
            "p1": "ἀγγέλλοιμεν",
            "p2": "ἀγγέλλοιτε",
            "p3": "ἀγγέλλοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "ἄγγελλε",
            "s3": "ἀγγελλέτω",
            "d2": "ἀγγέλλετον",
            "d3": "ἀγγελλέτων",
            "p1": "",
            "p2": "ἀγγέλλετε",
            "p3": "ἀγγελλόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἀγγέλλομαι",
            "s2": "ἀγγέλλῃ,ἀγγέλλει",
            "s3": "ἀγγέλλεται",
            "d2": "ἀγγέλλεσθον",
            "d3": "ἀγγέλλεσθον",
            "p1": "ἀγγελλόμεθᾰ",
            "p2": "ἀγγέλλεσθε",
            "p3": "ἀγγέλλονται"
          },
          "subjunctive": {
            "s1": "ἀγγέλλωμαι",
            "s2": "ἀγγέλλῃ",
            "s3": "ἀγγέλληται",
            "d2": "ἀγγέλλησθον",
            "d3": "ἀγγέλλησθον",
            "p1": "ἀγγελλώμεθᾰ",
            "p2": "ἀγγέλλησθε",
            "p3": "ἀγγέλλωνται"
          },
          "optative": {
            "s1": "ἀγγελλοίμην",
            "s2": "ἀγγέλλοιο",
            "s3": "ἀγγέλλοιτο",
            "d2": "ἀγγέλλοισθον",
            "d3": "ἀγγελλοίσθην",
            "p1": "ἀγγελλοίμεθᾰ",
            "p2": "ἀγγέλλοισθε",
            "p3": "ἀγγέλλοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "ἀγγέλλου",
            "s3": "ἀγγελλέσθω",
            "d2": "ἀγγέλλεσθον",
            "d3": "ἀγγελλέσθων",
            "p1": "",
            "p2": "ἀγγέλλεσθε",
            "p3": "ἀγγελλέσθων"
          }
        },
        "infinitive": {
          "active": "ἀγγέλλειν",
          "middle": "ἀγγέλλεσθαι"
        },
        "participle": {
          "m": {
            "active": "ἀγγέλλων",
            "middle": "ἀγγελλόμενος"
          },
          "f": {
            "active": "ἀγγέλλουσᾰ",
            "middle": "ἀγγελλομένη"
          },
          "n": {
            "active": "ἀγγέλλον",
            "middle": "ἀγγελλόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%81%CE%B8%CF%81%CE%BF%CE%AF%CE%B6%CF%89
    "type": "verb",
    "ground": "ἁθροίζειν",
    "passport": "ἁθροίζω",
    "dutch": "verzamelen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "ἁθροίζω",
            "s2": "ἁθροίζεις",
            "s3": "ἁθροίζει",
            "d2": "ἁθροίζετον",
            "d3": "ἁθροίζετον",
            "p1": "ἁθροίζομεν",
            "p2": "ἁθροίζετε",
            "p3": "ἁθροίζουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "ἁθροίζω",
            "s2": "ἁθροίζῃς",
            "s3": "ἁθροίζῃ",
            "d2": "ἁθροίζητον",
            "d3": "ἁθροίζητον",
            "p1": "ἁθροίζωμεν",
            "p2": "ἁθροίζητε",
            "p3": "ἁθροίζωσῐ(ν)"
          },
          "optative": {
            "s1": "ἁθροίζοιμῐ",
            "s2": "ἁθροίζοις",
            "s3": "ἁθροίζοι",
            "d2": "ἁθροίζοιτον",
            "d3": "ἁθροιζοίτην",
            "p1": "ἁθροίζοιμεν",
            "p2": "ἁθροίζοιτε",
            "p3": "ἁθροίζοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "ἅθροιζε",
            "s3": "ἁθροιζέτω",
            "d2": "ἁθροίζετον",
            "d3": "ἁθροιζέτων",
            "p1": "",
            "p2": "ἁθροίζετε",
            "p3": "ἁθροιζόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἁθροίζομαι",
            "s2": "ἁθροίζῃ,ἁθροίζει",
            "s3": "ἁθροίζεται",
            "d2": "ἁθροίζεσθον",
            "d3": "ἁθροίζεσθον",
            "p1": "ἁθροιζόμεθᾰ",
            "p2": "ἁθροίζεσθε",
            "p3": "ἁθροίζονται"
          },
          "subjunctive": {
            "s1": "ἁθροίζωμαι",
            "s2": "ἁθροίζῃ",
            "s3": "ἁθροίζηται",
            "d2": "ἁθροίζησθον",
            "d3": "ἁθροίζησθον",
            "p1": "ἁθροιζώμεθᾰ",
            "p2": "ἁθροίζησθε",
            "p3": "ἁθροίζωνται"
          },
          "optative": {
            "s1": "ἁθροιζοίμην",
            "s2": "ἁθροίζοιο",
            "s3": "ἁθροίζοιτο",
            "d2": "ἁθροίζοισθον",
            "d3": "ἁθροιζοίσθην",
            "p1": "ἁθροιζοίμεθᾰ",
            "p2": "ἁθροίζοισθε",
            "p3": "ἁθροίζοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "ἁθροίζου",
            "s3": "ἁθροιζέσθω",
            "d2": "ἁθροίζεσθον",
            "d3": "ἁθροιζέσθων",
            "p1": "",
            "p2": "ἁθροίζεσθε",
            "p3": "ἁθροιζέσθων"
          }
        },
        "infinitive": {
          "active": "ἁθροίζειν",
          "middle": "ἁθροίζεσθαι"
        },
        "participle": {
          "m": {
            "active": "ἁθροίζων",
            "middle": "ἁθροιζόμενος"
          },
          "f": {
            "active": "ἁθροίζουσᾰ",
            "middle": "ἁθροιζομένη"
          },
          "n": {
            "active": "ἁθροῖζον",
            "middle": "ἁθροιζόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἥθροιζον",
            "s2": "ἥθροιζες",
            "s3": "ἥθροιζε(ν)",
            "d2": "ἡθροίζετον",
            "d3": "ἡθροιζέτην",
            "p1": "ἡθροίζομεν",
            "p2": "ἡθροίζετε",
            "p3": "ἥθροιζον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἡθροιζόμην",
            "s2": "ἡθροίζου",
            "s3": "ἡθροίζετο",
            "d2": "ἡθροίζεσθον",
            "d3": "ἡθροιζέσθην",
            "p1": "ἡθροιζόμεθᾰ",
            "p2": "ἡθροίζεσθε",
            "p3": "ἡθροίζοντο"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "ἁθροίσω",
            "s2": "ἁθροίσεις",
            "s3": "ἁθροίσει",
            "d2": "ἁθροίσετον",
            "d3": "ἁθροίσετον",
            "p1": "ἁθροίσομεν",
            "p2": "ἁθροίσετε",
            "p3": "ἁθροίσουσῐ(ν)"
          },
          "optative": {
            "s1": "ἁθροίσοιμῐ",
            "s2": "ἁθροίσοις",
            "s3": "ἁθροίσοι",
            "d2": "ἁθροίσοιτον",
            "d3": "ἁθροισοίτην",
            "p1": "ἁθροίσοιμεν",
            "p2": "ἁθροίσοιτε",
            "p3": "ἁθροίσοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "ἁθροίσομαι",
            "s2": "ἁθροίσῃ,ἁθροίσει",
            "s3": "ἁθροίσεται",
            "d2": "ἁθροίσεσθον",
            "d3": "ἁθροίσεσθον",
            "p1": "ἁθροισόμεθᾰ",
            "p2": "ἁθροίσεσθε",
            "p3": "ἁθροίσονται"
          },
          "optative": {
            "s1": "ἁθροισοίμην",
            "s2": "ἁθροίσοιο",
            "s3": "ἁθροίσοιτο",
            "d2": "ἁθροίσοισθον",
            "d3": "ἁθροισοίσθην",
            "p1": "ἁθροισοίμεθᾰ",
            "p2": "ἁθροίσοισθε",
            "p3": "ἁθροίσοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "ἁθροισήσομαι",
            "s2": "ἁθροισήσῃ",
            "s3": "ἁθροισήσεται",
            "d2": "ἁθροισήσεσθον",
            "d3": "ἁθροισήσεσθον",
            "p1": "ἁθροισησόμεθᾰ",
            "p2": "ἁθροισήσεσθε",
            "p3": "ἁθροισήσονται"
          },
          "optative": {
            "s1": "ἁθροισησοίμην",
            "s2": "ἁθροισήσοιο",
            "s3": "ἁθροισήσοιτο",
            "d2": "ἁθροισήσοισθον",
            "d3": "ἁθροισησοίσθην",
            "p1": "ἁθροισησοίμεθᾰ",
            "p2": "ἁθροισήσοισθε",
            "p3": "ἁθροισήσοιντο"
          }
        },
        "infinitive": {
          "active": "ἁθροίσειν",
          "middle": "ἁθροίσεσθαι",
          "passive": "ἁθροισήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "ἁθροίσων",
            "middle": "ἁθροισόμενος",
            "passive": "ἁθροισησόμενος"
          },
          "f": {
            "active": "ἁθροίσουσᾰ",
            "middle": "ἁθροισομένη",
            "passive": "ἁθροισησομένη"
          },
          "n": {
            "active": "ἁθροῖσον",
            "middle": "ἁθροισόμενον",
            "passive": "ἁθροισησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CE%BD%CE%BF%CE%AF%CE%B3%CF%89
    "type": "verb",
    "ground": "ᾰ̓νοίγειν",
    "passport": "ᾰ̓νοίγω",
    "dutch": "openen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓νοίγω",
            "s2": "ᾰ̓νοίγεις",
            "s3": "ᾰ̓νοίγει",
            "d2": "ᾰ̓νοίγετον",
            "d3": "ᾰ̓νοίγετον",
            "p1": "ᾰ̓νοίγομεν",
            "p2": "ᾰ̓νοίγετε",
            "p3": "ᾰ̓νοίγουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "ᾰ̓νοίγω",
            "s2": "ᾰ̓νοίγῃς",
            "s3": "ᾰ̓νοίγῃ",
            "d2": "ᾰ̓νοίγητον",
            "d3": "ᾰ̓νοίγητον",
            "p1": "ᾰ̓νοίγωμεν",
            "p2": "ᾰ̓νοίγητε",
            "p3": "ᾰ̓νοίγωσῐ(ν)"
          },
          "optative": {
            "s1": "ᾰ̓νοίγοιμῐ",
            "s2": "ᾰ̓νοίγοις",
            "s3": "ᾰ̓νοίγοι",
            "d2": "ᾰ̓νοίγοιτον",
            "d3": "ᾰ̓νοιγοίτην",
            "p1": "ᾰ̓νοίγοιμεν",
            "p2": "ᾰ̓νοίγοιτε",
            "p3": "ᾰ̓νοίγοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "ᾰ̓νοιγε",
            "s3": "ᾰ̓νοιγέτω",
            "d2": "ᾰ̓νοίγετον",
            "d3": "ᾰ̓νοιγέτων",
            "p1": "",
            "p2": "ᾰ̓νοίγετε",
            "p3": "ᾰ̓νοιγόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ᾰ̓νοίγομαι",
            "s2": "ᾰ̓νοίγῃ,ᾰ̓νοίγει",
            "s3": "ᾰ̓νοίγεται",
            "d2": "ᾰ̓νοίγεσθον",
            "d3": "ᾰ̓νοίγεσθον",
            "p1": "ᾰ̓νοιγόμεθᾰ",
            "p2": "ᾰ̓νοίγεσθε",
            "p3": "ᾰ̓νοίγονται"
          },
          "subjunctive": {
            "s1": "ᾰ̓νοίγωμαι",
            "s2": "ᾰ̓νοίγῃ",
            "s3": "ᾰ̓νοίγηται",
            "d2": "ᾰ̓νοίγησθον",
            "d3": "ᾰ̓νοίγησθον",
            "p1": "ᾰ̓νοιγώμεθᾰ",
            "p2": "ᾰ̓νοίγησθε",
            "p3": "ᾰ̓νοίγωνται"
          },
          "optative": {
            "s1": "ᾰ̓νοιγοίμην",
            "s2": "ᾰ̓νοίγοιο",
            "s3": "ᾰ̓νοίγοιτο",
            "d2": "ᾰ̓νοίγοισθον",
            "d3": "ᾰ̓νοιγοίσθην",
            "p1": "ᾰ̓νοιγοίμεθᾰ",
            "p2": "ᾰ̓νοίγοισθε",
            "p3": "ᾰ̓νοίγοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "ᾰ̓νοίγου",
            "s3": "ᾰ̓νοιγέσθω",
            "d2": "ᾰ̓νοίγεσθον",
            "d3": "ᾰ̓νοιγέσθων",
            "p1": "",
            "p2": "ᾰ̓νοίγεσθε",
            "p3": "ᾰ̓νοιγέσθων"
          }
        },
        "infinitive": {
          "active": "ᾰ̓νοίγειν",
          "middle": "ᾰ̓νοίγεσθαι"
        },
        "participle": {
          "m": {
            "active": "ᾰ̓νοίγων",
            "middle": "ᾰ̓νοιγόμενος"
          },
          "f": {
            "active": "ᾰ̓νοίγουσᾰ",
            "middle": "ᾰ̓νοιγομένη"
          },
          "n": {
            "active": "ᾰ̓νοῖγον",
            "middle": "ᾰ̓νοιγόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓νέῳγον",
            "s2": "ᾰ̓νέῳγες",
            "s3": "ᾰ̓νέῳγε(ν)",
            "d2": "ᾰ̓νεῴγετον",
            "d3": "ᾰ̓νεῳγέτην",
            "p1": "ᾰ̓νεῴγομεν",
            "p2": "ᾰ̓νεῴγετε",
            "p3": "ᾰ̓νέῳγον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ᾰ̓νεῳγόμην",
            "s2": "ᾰ̓νεῴγου",
            "s3": "ᾰ̓νεῴγετο",
            "d2": "ᾰ̓νεῴγεσθον",
            "d3": "ᾰ̓νεῳγέσθην",
            "p1": "ᾰ̓νεῳγόμεθᾰ",
            "p2": "ᾰ̓νεῴγεσθε",
            "p3": "ᾰ̓νεῴγοντο"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "ᾰ̓νοίξω",
            "s2": "ᾰ̓νοίξεις",
            "s3": "ᾰ̓νοίξει",
            "d2": "ᾰ̓νοίξετον",
            "d3": "ᾰ̓νοίξετον",
            "p1": "ᾰ̓νοίξομεν",
            "p2": "ᾰ̓νοίξετε",
            "p3": "ᾰ̓νοίξουσῐ(ν)"
          },
          "optative": {
            "s1": "ᾰ̓νοίξοιμῐ",
            "s2": "ᾰ̓νοίξοις",
            "s3": "ᾰ̓νοίξοι",
            "d2": "ᾰ̓νοίξοιτον",
            "d3": "ᾰ̓νοιξοίτην",
            "p1": "ᾰ̓νοίξοιμεν",
            "p2": "ᾰ̓νοίξοιτε",
            "p3": "ᾰ̓νοίξοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "ᾰ̓νοίξομαι",
            "s2": "ᾰ̓νοίξῃ,ᾰ̓νοίξει",
            "s3": "ᾰ̓νοίξεται",
            "d2": "ᾰ̓νοίξεσθον",
            "d3": "ᾰ̓νοίξεσθον",
            "p1": "ᾰ̓νοιξόμεθᾰ",
            "p2": "ᾰ̓νοίξεσθε",
            "p3": "ᾰ̓νοίξονται"
          },
          "optative": {
            "s1": "ᾰ̓νοιξοίμην",
            "s2": "ᾰ̓νοίξοιο",
            "s3": "ᾰ̓νοίξοιτο",
            "d2": "ᾰ̓νοίξοισθον",
            "d3": "ᾰ̓νοιξοίσθην",
            "p1": "ᾰ̓νοιξοίμεθᾰ",
            "p2": "ᾰ̓νοίξοισθε",
            "p3": "ᾰ̓νοίξοιντο"
          }
        },
        "infinitive": {
          "active": "ᾰ̓νοίξειν",
          "middle": "ᾰ̓νοίξεσθαι",
          "passive": ""
        },
        "participle": {
          "m": {
            "active": "ᾰ̓νοίξων",
            "middle": "ᾰ̓νοιξόμενος",
            "passive": ""
          },
          "f": {
            "active": "ᾰ̓νοίξουσᾰ",
            "middle": "ᾰ̓νοιξομένη",
            "passive": ""
          },
          "n": {
            "active": "ᾰ̓νοῖξον",
            "middle": "ᾰ̓νοιξόμενον",
            "passive": ""
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BD%CE%BF%CE%BC%CE%AF%CE%B6%CF%89
    "type": "verb",
    "ground": "νομῐζειν",
    "passport": "νομῐζω",
    "dutch": "menen; (+ 2 acc.) (iem.) beschouwen (als)",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "νομῐζω",
            "s2": "νομῐζεις",
            "s3": "νομῐζει",
            "d2": "νομῐζετον",
            "d3": "νομῐζετον",
            "p1": "νομῐζομεν",
            "p2": "νομῐζετε",
            "p3": "νομῐζουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "νομῐζω",
            "s2": "νομῐζῃς",
            "s3": "νομῐζῃ",
            "d2": "νομῐζητον",
            "d3": "νομῐζητον",
            "p1": "νομῐζωμεν",
            "p2": "νομῐζητε",
            "p3": "νομῐζωσῐ(ν)"
          },
          "optative": {
            "s1": "νομῐζοιμῐ",
            "s2": "νομῐζοις",
            "s3": "νομῐζοι",
            "d2": "νομῐζοιτον",
            "d3": "νομῐζοίτην",
            "p1": "νομῐζοιμεν",
            "p2": "νομῐζοιτε",
            "p3": "νομῐζοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "νόμῐζε",
            "s3": "νομῐζέτω",
            "d2": "νομῐζετον",
            "d3": "νομῐζέτων",
            "p1": "",
            "p2": "νομῐζετε",
            "p3": "νομῐζόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "νομῐζομαι",
            "s2": "νομῐζῃ,νομῐζει",
            "s3": "νομῐζεται",
            "d2": "νομῐζεσθον",
            "d3": "νομῐζεσθον",
            "p1": "νομῐζόμεθᾰ",
            "p2": "νομῐζεσθε",
            "p3": "νομῐζονται"
          },
          "subjunctive": {
            "s1": "νομῐζωμαι",
            "s2": "νομῐζῃ",
            "s3": "νομῐζηται",
            "d2": "νομῐζησθον",
            "d3": "νομῐζησθον",
            "p1": "νομῐζώμεθᾰ",
            "p2": "νομῐζησθε",
            "p3": "νομῐζωνται"
          },
          "optative": {
            "s1": "νομῐζοίμην",
            "s2": "νομῐζοιο",
            "s3": "νομῐζοιτο",
            "d2": "νομῐζοισθον",
            "d3": "νομῐζοίσθην",
            "p1": "νομῐζοίμεθᾰ",
            "p2": "νομῐζοισθε",
            "p3": "νομῐζοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "νομῐζου",
            "s3": "νομῐζέσθω",
            "d2": "νομῐζεσθον",
            "d3": "νομῐζέσθων",
            "p1": "",
            "p2": "νομῐζεσθε",
            "p3": "νομῐζέσθων"
          }
        },
        "infinitive": {
          "active": "νομῐζειν",
          "middle": "νομῐζεσθαι"
        },
        "participle": {
          "m": {
            "active": "νομῐζων",
            "middle": "νομῐζόμενος"
          },
          "f": {
            "active": "νομῐζουσᾰ",
            "middle": "νομῐζομένη"
          },
          "n": {
            "active": "νομῐζον",
            "middle": "νομῐζόμενον"
          }
        }
      },
      "imperfect": {
        "active": {
          "indicative": {
            "s1": "ἐνόμῐζον",
            "s2": "ἐνόμῐζες",
            "s3": "ἐνόμῐζε(ν)",
            "d2": "ἐνομῐζετον",
            "d3": "ἐνομῐζέτην",
            "p1": "ἐνομῐζομεν",
            "p2": "ἐνομῐζετε",
            "p3": "ἐνόμῐζον"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "ἐνομῐζόμην",
            "s2": "ἐνομῐζου",
            "s3": "ἐνομῐζετο",
            "d2": "ἐνομῐζεσθον",
            "d3": "ἐνομῐζέσθην",
            "p1": "ἐνομῐζόμεθᾰ",
            "p2": "ἐνομῐζεσθε",
            "p3": "ἐνομῐζοντο"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BA%CE%B5%CE%BB%CE%B5%CF%8D%CF%89
    "type": "verb",
    "ground": "κελεύειν",
    "passport": "κελεύω",
    "dutch": "aansporen, bevelen",
    "explicitConjugations": {
      "present": {
        "active": {
          "indicative": {
            "s1": "κελεύω",
            "s2": "κελεύεις",
            "s3": "κελεύει",
            "d2": "κελεύετον",
            "d3": "κελεύετον",
            "p1": "κελεύομεν",
            "p2": "κελεύετε",
            "p3": "κελεύουσῐ(ν)"
          },
          "subjunctive": {
            "s1": "κελεύω",
            "s2": "κελεύῃς",
            "s3": "κελεύῃ",
            "d2": "κελεύητον",
            "d3": "κελεύητον",
            "p1": "κελεύωμεν",
            "p2": "κελεύητε",
            "p3": "κελεύωσῐ(ν)"
          },
          "optative": {
            "s1": "κελεύοιμῐ",
            "s2": "κελεύοις",
            "s3": "κελεύοι",
            "d2": "κελεύοιτον",
            "d3": "κελευοίτην",
            "p1": "κελεύοιμεν",
            "p2": "κελεύοιτε",
            "p3": "κελεύοιεν"
          },
          "imperative": {
            "s1": "",
            "s2": "κέλευε",
            "s3": "κελευέτω",
            "d2": "κελεύετον",
            "d3": "κελευέτων",
            "p1": "",
            "p2": "κελεύετε",
            "p3": "κελευόντων"
          }
        },
        "middlePassive": {
          "indicative": {
            "s1": "κελεύομαι",
            "s2": "κελεύῃ,κελεύει",
            "s3": "κελεύεται",
            "d2": "κελεύεσθον",
            "d3": "κελεύεσθον",
            "p1": "κελευόμεθᾰ",
            "p2": "κελεύεσθε",
            "p3": "κελεύονται"
          },
          "subjunctive": {
            "s1": "κελεύωμαι",
            "s2": "κελεύῃ",
            "s3": "κελεύηται",
            "d2": "κελεύησθον",
            "d3": "κελεύησθον",
            "p1": "κελευώμεθᾰ",
            "p2": "κελεύησθε",
            "p3": "κελεύωνται"
          },
          "optative": {
            "s1": "κελευοίμην",
            "s2": "κελεύοιο",
            "s3": "κελεύοιτο",
            "d2": "κελεύοισθον",
            "d3": "κελευοίσθην",
            "p1": "κελευοίμεθᾰ",
            "p2": "κελεύοισθε",
            "p3": "κελεύοιντο"
          },
          "imperative": {
            "s1": "",
            "s2": "κελεύου",
            "s3": "κελευέσθω",
            "d2": "κελεύεσθον",
            "d3": "κελευέσθων",
            "p1": "",
            "p2": "κελεύεσθε",
            "p3": "κελευέσθων"
          }
        },
        "infinitive": {
          "active": "κελεύειν",
          "middle": "κελεύεσθαι"
        },
        "participle": {
          "m": {
            "active": "κελεύων",
            "middle": "κελευόμενος"
          },
          "f": {
            "active": "κελεύουσᾰ",
            "middle": "κελευομένη"
          },
          "n": {
            "active": "κελεῦον",
            "middle": "κελευόμενον"
          }
        }
      },
      "future": {
        "active": {
          "indicative": {
            "s1": "κελεύσω",
            "s2": "κελεύσεις",
            "s3": "κελεύσει",
            "d2": "κελεύσετον",
            "d3": "κελεύσετον",
            "p1": "κελεύσομεν",
            "p2": "κελεύσετε",
            "p3": "κελεύσουσῐ(ν)"
          },
          "optative": {
            "s1": "κελεύσοιμῐ",
            "s2": "κελεύσοις",
            "s3": "κελεύσοι",
            "d2": "κελεύσοιτον",
            "d3": "κελευσοίτην",
            "p1": "κελεύσοιμεν",
            "p2": "κελεύσοιτε",
            "p3": "κελεύσοιεν"
          }
        },
        "middle": {
          "indicative": {
            "s1": "κελεύσομαι",
            "s2": "κελεύσῃ,κελεύσει",
            "s3": "κελεύσεται",
            "d2": "κελεύσεσθον",
            "d3": "κελεύσεσθον",
            "p1": "κελευσόμεθᾰ",
            "p2": "κελεύσεσθε",
            "p3": "κελεύσονται"
          },
          "optative": {
            "s1": "κελευσοίμην",
            "s2": "κελεύσοιο",
            "s3": "κελεύσοιτο",
            "d2": "κελεύσοισθον",
            "d3": "κελευσοίσθην",
            "p1": "κελευσοίμεθᾰ",
            "p2": "κελεύσοισθε",
            "p3": "κελεύσοιντο"
          }
        },
        "passive": {
          "indicative": {
            "s1": "κελευσθήσομαι",
            "s2": "κελευσθήσῃ",
            "s3": "κελευσθήσεται",
            "d2": "κελευσθήσεσθον",
            "d3": "κελευσθήσεσθον",
            "p1": "κελευσθησόμεθᾰ",
            "p2": "κελευσθήσεσθε",
            "p3": "κελευσθήσονται"
          },
          "optative": {
            "s1": "κελευσθησοίμην",
            "s2": "κελευσθήσοιο",
            "s3": "κελευσθήσοιτο",
            "d2": "κελευσθήσοισθον",
            "d3": "κελευσθησοίσθην",
            "p1": "κελευσθησοίμεθᾰ",
            "p2": "κελευσθήσοισθε",
            "p3": "κελευσθήσοιντο"
          }
        },
        "infinitive": {
          "active": "κελεύσειν",
          "middle": "κελεύσεσθαι",
          "passive": "κελευσθήσεσθαι"
        },
        "participle": {
          "m": {
            "active": "κελεύσων",
            "middle": "κελευσόμενος",
            "passive": "κελευσθησόμενος"
          },
          "f": {
            "active": "κελεύσουσᾰ",
            "middle": "κελευσομένη",
            "passive": "κελευσθησομένη"
          },
          "n": {
            "active": "κελεῦσον",
            "middle": "κελευσόμενον",
            "passive": "κελευσθησόμενον"
          }
        }
      }
    }
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CE%AD%CF%89
    type: "verb",
    dutch: "missen, nodig hebben, vragen (om)",
    ground: "δεῖσθαι",
    passport: "δέ-ομαι (+ gen.)",
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%BF%CE%B9%CE%AD%CF%89
    type: "verb",
    dutch: "doen, maken",
    ground: "ποιεῖν",
    passport: "ποιέ-ω",
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CE%B4%CE%B9%CE%BA%CE%AD%CF%89
    type: "verb",
    dutch: "onrechtvaardig (be)handelen",
    ground: "ἀδῐκεῖν",
    passport: "ἀδῐκέ-ω",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B2%CE%BF%CE%B7%CE%B8%CE%AD%CF%89
    type: "verb",
    dutch: "helpen",
    ground: "βοηθεῖν",
    passport: "βοηθέ-ω (+ dat.)",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CE%BF%CE%BA%CE%AD%CF%89
    type: "verb",
    dutch: "schijnen, de indruk wekken, menen",
    ground: "δοκεῖν",
    passport: "δοκέ-ω",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BA%CE%B1%CE%BB%CE%AD%CF%89
    type: "verb",
    dutch: "noemen, roepen, uitnodigen",
    ground: "καλεῖν",
    passport: "καλέ-ω",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%BF%E1%BC%B0%CE%BA%CE%AD%CF%89
    type: "verb",
    dutch: "wonen, bewonen",
    ground: "οἰκεῖν",
    passport: "οἰκέ-ω",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CE%B5%CE%AF%CE%BA%CE%BD%CF%85%CE%BC%CE%B9
    type: "verb",
    dutch: "tonen, bewijzen",
    ground: "δεικνῠ́ναι",
    passport: "δείκνῡμῐ",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CE%AF%CE%B4%CF%89%CE%BC%CE%B9
    type: "verb",
    dutch: "geven",
    ground: "δῐδόναι",
    passport: "δῐ́δωμῐ",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B4%CF%8D%CE%BD%CE%B1%CE%BC%CE%B1%CE%B9
    type: "verb",
    dutch: "kunnen",
    ground: "δῠ́νᾰσθαι",
    passport: "δῠ́νᾰμαι",
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%90%CF%80%CE%AF%CF%83%CF%84%CE%B1%CE%BC%CE%B1%CE%B9
    type: "verb",
    dutch: "weten, kunnen",
    ground: "ἐπίστᾰσθαι",
    passport: "ἐπίστᾰμαι",
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%B5%CE%B7%CE%BC%CE%B9
    type: "verb",
    dutch: "zenden",
    ground: "ῑ̔έναι",
    passport: "ῑ̔́ημῐ",
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%B5%CF%83%CF%84%CE%B7%CE%BC%CE%B9
    type: "verb",
    dutch: "doen staan, aanstellen",
    ground: "ῐ̔στᾰ́ναι",
    passport: "ῐ̔́στημῐ",
  },
  {
    // https://en.wiktionary.org/wiki/%CF%84%CE%AF%CE%B8%CE%B7%CE%BC%CE%B9
    type: "verb",
    dutch: "plaatsen, leggen",
    ground: "τῐθέναι",
    passport: "τῐ́θημῐ",
  },
  {
    // https://en.wiktionary.org/wiki/%E1%BC%80%CF%86%CE%B9%CE%BA%CE%BD%CE%AD%CE%BF%CE%BC%CE%B1%CE%B9
    type: "verb",
    dutch: "aankomen",
    ground: "ἀφικνεῖσθαι",
    passport: "ἀφ-ῐκνέ-ομαι",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B6%CE%B7%CF%84%CE%AD%CF%89
    type: "verb",
    dutch: "zoeken, onderzoeken, vragen",
    ground: "ζητεῖν",
    passport: "ζητέ-ω",
  },
  {
    // https://en.wiktionary.org/wiki/%CF%86%CE%BF%CE%B2%CE%AD%CF%89
    type: "verb",
    dutch: "schrik aanjagen",
    ground: "φοβεῖν",
    passport: "φοβέ-ω",
  },
  {
    // https://en.wiktionary.org/wiki/%CF%86%CE%BF%CE%B2%CE%AD%CF%89#Ancient_Greek
    type: "verb",
    dutch: "bang zijn",
    ground: "φοβεῖσθαι",
    passport: "φοβέ-ομαι",
  },
  {
    // https://en.wiktionary.org/wiki/%CE%B1%E1%BC%B0%CF%84%CE%AD%CF%89
    type: "verb",
    dutch: "vragen (om te krijgen)",
    ground: "αἰτεῖν",
    passport: "αἰτέ-ω",
  },
  {
    // https://en.wiktionary.org/wiki/%CF%86%CF%8D%CF%89
    type: "verb",
    dutch: "doen onstaan",
    ground: "φύειν",
    passport: "φύω",
  },
  {
    // https://en.wiktionary.org/wiki/%CF%86%CF%8D%CF%89
    type: "verb",
    dutch: "ontstaan, groeien",
    ground: "φύεσθαι",
    passport: "φύομαι",
  },
  {
    // https://en.wiktionary.org/wiki/%CF%80%CE%AC%CF%81%CE%B5%CE%B9%CE%BC%CE%B9
    type: "verb",
    dutch: "aanwezig zijn",
    ground: "παρεῖναι",
    passport: "πάρ-ειμι",
  },
  // {
  //   type: "verb",
  //   dutch: "",
  //   ground: "",
  //   passport: "",
  //   explicitConjugations: {
  //     active: {
  //       indicative: {
  //         present: {
  //           "s1": "",
  //           "s2": "",
  //           "s3": "",
  //           "d2": "",
  //           "d3": "",
  //           "p1": "",
  //           "p2": "",
  //           "p3": "",
  //         },
  //       },
  //     },
  //   },
  // },
];

export const searchGreekVerb = searchWord(GREEK_VERBS, "Greek verb", "ground");